import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import SearchIcon from "@mui/icons-material/Search";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://panel.haymestasarim.com/api";

const ORDER_POLL_INTERVAL_MS = 2500;
const WAITER_POLL_INTERVAL_MS = 2500;
const PAGE_SIZE = 10;

const BRAND = {
  navy: "#0f172a",
  blue: "#2563eb",
  blueDark: "#0b4d8b",
  orange: "#f97316",
  orangeDark: "#c2410c",
  green: "#16a34a",
  red: "#dc2626",
};

const TEXT = {
  tr: {
    pageTitle: "Sipariş Yönetimi",
    pageSubtitle:
      "QR menü siparişlerini, garson çağrılarını, ödeme taleplerini, stok ve ciro takibini tek ekrandan yönetin.",
    soundOn: "Sesli uyarı açık",
    soundOff: "Sesli uyarı kapalı",
    waiterCalls: "Garson Çağrıları",
    refresh: "Siparişleri Yenile",
    search:
      "Sipariş ID, masa, müşteri adı veya telefon ara",
    all: "Tümü",
    new: "Yeni",
    preparing: "Hazırlanıyor",
    completed: "Tamamlandı",
    cancelled: "İptal",
    table: "Masa",
    customer: "Müşteri",
    phone: "Telefon",
    note: "Not",
    total: "Toplam",
    piece: "adet",
    markPreparing: "Hazırlanıyor",
    markCompleted: "Tamamlandı",
    cancel: "İptal Et",
    delete: "Sil",
    openCash: "Kasada Aç",
    operationCenter: "İşlem Merkezi",
    approveAndPrint: "Onayla ve Mutfağa Yazdır",
    preparingOnly: "Sadece Hazırlanıyor Yap",
    kitchenPrint: "Mutfak Fişi Yazdır",
    kitchenReprint: "Mutfak Fişini Tekrar Yazdır",
    kitchenPrintTitle: "Mutfak Fişi",
    kitchenPrintSubtitle:
      "Giant 100D için 80 mm varsayılandır; diğer yazıcılar için kâğıt ölçüsünü seçebilirsiniz.",
    paperSize: "Kâğıt / Yazıcı Ölçüsü",
    paper58: "58 mm Termal",
    paper80: "80 mm Termal",
    paperA4: "A4 / Standart Yazıcı",
    includePrices: "Fişte fiyatları göster",
    printNow: "Yazdırma Ekranını Aç",
    printHelp:
      "Yazdırma ekranında işletim sistemine tanıtılmış USB veya ağ yazıcınızı seçin.",
    popupBlocked:
      "Yazdırma penceresi açılamadı. Tarayıcıdaki açılır pencere iznini kontrol edin.",
    printOpened: "Mutfak fişi yazdırma ekranı açıldı.",
    kitchenReceipt: "MUTFAK SİPARİŞ FİŞİ",
    orderNo: "Sipariş No",
    orderTime: "Sipariş Zamanı",
    printedAt: "Yazdırma Zamanı",
    products: "ÜRÜNLER",
    noItems: "Ürün bilgisi bulunamadı.",
    preparationNote: "SİPARİŞ NOTU",
    noOrders: "Henüz sipariş yok.",
    noFiltered:
      "Filtrelerle eşleşen sipariş bulunamadı.",
    noWaiter: "Henüz garson çağrısı yok.",
    guest: "Misafir",
    unspecified: "Belirtilmemiş",
    markRead: "Okundu İşaretle",
    seen: "İşlem Görüldü",
    close: "Kapat",
    retry: "Yeniden Dene",
    cancelTitle: "Sipariş iptal edilsin mi?",
    cancelText:
      "Bu işlem siparişi iptal olarak işaretler. İşlemden sonra sipariş listesi yenilenir.",
    giveUp: "Vazgeç",
    statusUpdated:
      "Sipariş #{id} durumu güncellendi.",
    statusAndCash:
      "Sipariş #{id} tamamlandı ve kasaya aktarıldı.",
    cashAlready:
      "Bu sipariş daha önce kasaya aktarılmış.",
    cashFail:
      "Durum güncellendi fakat kasa aktarımı yapılamadı.",
    cashZero:
      "Tutar sıfır olduğu için kasaya aktarılmadı.",
    newOrderMsg: "{count} yeni sipariş geldi!",
    newWaiterMsg:
      "{count} yeni garson çağrısı!",
    totalOrders: "Toplam Sipariş",
    newOrders: "Yeni Sipariş",
    dailyRevenue: "Günlük Ciro",
    criticalStock: "Kritik Stok",
    orderList: "Gelen Siparişler",
    orderListSub:
      "Sipariş özetine dokunarak ürün ve işlem detaylarını açın.",
    paymentRequests: "Masa Ödeme Talepleri",
    paymentSub:
      "QR menüden gelen ödeme, hesap bölme ve bahşiş talepleri",
    waitingRequest: "bekleyen talep",
    noPayment:
      "Şu anda bekleyen masa ödeme talebi yok.",
    paymentType: "Ödeme türü",
    tip: "Bahşiş",
    cashPaid: "Nakit Ödendi",
    cardPaid: "Kart Ödendi",
    reject: "Reddet",
    stockManagement: "Stok Yönetimi",
    stockHelp:
      "Menü ürünlerinin stok miktarını ve minimum uyarı seviyesini yönetin.",
    addStock: "Stok Ekle",
    productName: "Ürün adı",
    stockQty: "Stok Adedi",
    minStock: "Minimum Uyarı",
    save: "Kaydet",
    critical: "Kritik / Tükendi",
    normal: "Stok Normal",
    noStock:
      "Henüz stok girilmemiş. Stok Ekle butonuyla ürün stoklarını oluşturabilirsiniz.",
    stockNameRequired:
      "Ürün adı boş olamaz.",
    stockSaved:
      "{name} stok bilgisi kaydedildi.",
    stockDeleted: "Stok kaydı silindi.",
    stockSaveFail: "Stok kaydedilemedi.",
    stockDeleteFail: "Stok silinemedi.",
    reports: "Satış ve Ciro Raporları",
    reportsSub:
      "Sipariş adedi, ciro ve en çok satan ürünleri takip edin.",
    daily: "Günlük",
    monthly: "Aylık",
    period: "Dönem",
    orderCount: "Sipariş",
    revenue: "Ciro",
    topProducts: "En Çok Satan Ürünler",
    noSales: "Henüz satış verisi yok.",
    loadingPayments:
      "Ödeme talepleri yükleniyor...",
    newCall: "Yeni Çağrı",
    waiterSubtitle:
      "Masalardan gelen çağrıları hızlıca yönetin.",
    waiterEmptySubtitle:
      "Tüm masalar şu anda sakin görünüyor.",
    touchToClose:
      "Kapatmak için dokunabilirsiniz.",
    deleteConfirm:
      "#{id} siparişi tamamen silinsin mi?",
    deleteSuccess:
      "Sipariş #{id} tamamen silindi.",
    deleteFail: "Sipariş silinemedi.",
  },

  en: {
    pageTitle: "Order Management",
    pageSubtitle:
      "Manage QR menu orders, waiter calls, payment requests, stock and revenue from one screen.",
    soundOn: "Sound alert on",
    soundOff: "Sound alert off",
    waiterCalls: "Waiter Calls",
    refresh: "Refresh Orders",
    search:
      "Search order ID, table, customer or phone",
    all: "All",
    new: "New",
    preparing: "Preparing",
    completed: "Completed",
    cancelled: "Cancelled",
    table: "Table",
    customer: "Customer",
    phone: "Phone",
    note: "Note",
    total: "Total",
    piece: "pcs",
    markPreparing: "Preparing",
    markCompleted: "Completed",
    cancel: "Cancel",
    delete: "Delete",
    openCash: "Open Cash",
    operationCenter: "Action Center",
    approveAndPrint: "Approve & Print to Kitchen",
    preparingOnly: "Mark Preparing Only",
    kitchenPrint: "Print Kitchen Ticket",
    kitchenReprint: "Reprint Kitchen Ticket",
    kitchenPrintTitle: "Kitchen Ticket",
    kitchenPrintSubtitle:
      "80 mm is the default for Giant 100D; choose another paper size for other printers.",
    paperSize: "Paper / Printer Size",
    paper58: "58 mm Thermal",
    paper80: "80 mm Thermal",
    paperA4: "A4 / Standard Printer",
    includePrices: "Show prices on ticket",
    printNow: "Open Print Dialog",
    printHelp:
      "Select any USB or network printer installed in your operating system.",
    popupBlocked:
      "The print window could not be opened. Check the browser pop-up permission.",
    printOpened: "Kitchen ticket print dialog opened.",
    kitchenReceipt: "KITCHEN ORDER TICKET",
    orderNo: "Order No",
    orderTime: "Order Time",
    printedAt: "Printed At",
    products: "ITEMS",
    noItems: "No item information found.",
    preparationNote: "ORDER NOTE",
    noOrders: "No orders yet.",
    noFiltered:
      "No orders match the filters.",
    noWaiter: "No waiter calls yet.",
    guest: "Guest",
    unspecified: "Not specified",
    markRead: "Mark as Read",
    seen: "Handled",
    close: "Close",
    retry: "Try Again",
    cancelTitle: "Cancel this order?",
    cancelText:
      "This marks the order as cancelled. The order list is refreshed afterwards.",
    giveUp: "Back",
    statusUpdated:
      "Order #{id} status updated.",
    statusAndCash:
      "Order #{id} completed and transferred to cash.",
    cashAlready:
      "This order was already transferred to cash.",
    cashFail:
      "Status updated but cash transfer failed.",
    cashZero:
      "Amount is zero, so it was not transferred to cash.",
    newOrderMsg:
      "{count} new order received!",
    newWaiterMsg:
      "{count} new waiter call!",
    totalOrders: "Total Orders",
    newOrders: "New Orders",
    dailyRevenue: "Daily Revenue",
    criticalStock: "Critical Stock",
    orderList: "Incoming Orders",
    orderListSub:
      "Tap an order summary to open products and actions.",
    paymentRequests:
      "Table Payment Requests",
    paymentSub:
      "Payment, bill split and tip requests from the QR menu",
    waitingRequest: "pending request",
    noPayment:
      "There are no pending table payment requests.",
    paymentType: "Payment type",
    tip: "Tip",
    cashPaid: "Cash Paid",
    cardPaid: "Card Paid",
    reject: "Reject",
    stockManagement: "Stock Management",
    stockHelp:
      "Manage menu stock quantities and minimum warning levels.",
    addStock: "Add Stock",
    productName: "Product name",
    stockQty: "Stock Quantity",
    minStock: "Minimum Alert",
    save: "Save",
    critical: "Critical / Out",
    normal: "Stock Normal",
    noStock:
      "No stock has been entered yet. Use Add Stock to create records.",
    stockNameRequired:
      "Product name cannot be empty.",
    stockSaved:
      "{name} stock information saved.",
    stockDeleted: "Stock record deleted.",
    stockSaveFail:
      "Stock could not be saved.",
    stockDeleteFail:
      "Stock could not be deleted.",
    reports: "Sales and Revenue Reports",
    reportsSub:
      "Track order count, revenue and best-selling products.",
    daily: "Daily",
    monthly: "Monthly",
    period: "Period",
    orderCount: "Orders",
    revenue: "Revenue",
    topProducts: "Best Selling Products",
    noSales: "No sales data yet.",
    loadingPayments:
      "Loading payment requests...",
    newCall: "New Call",
    waiterSubtitle:
      "Manage table calls quickly.",
    waiterEmptySubtitle:
      "All tables look calm right now.",
    touchToClose: "Tap to close.",
    deleteConfirm:
      "Permanently delete order #{id}?",
    deleteSuccess:
      "Order #{id} permanently deleted.",
    deleteFail:
      "Order could not be deleted.",
  },

  de: {
    pageTitle: "Bestellverwaltung",
    pageSubtitle:
      "QR-Bestellungen, Kellnerrufe, Zahlungsanfragen, Lager und Umsatz zentral verwalten.",
    soundOn:
      "Tonbenachrichtigung aktiv",
    soundOff: "Tonbenachrichtigung aus",
    waiterCalls: "Kellnerrufe",
    refresh:
      "Bestellungen aktualisieren",
    search:
      "Bestell-ID, Tisch, Kunde oder Telefon suchen",
    all: "Alle",
    new: "Neu",
    preparing: "In Vorbereitung",
    completed: "Abgeschlossen",
    cancelled: "Storniert",
    table: "Tisch",
    customer: "Kunde",
    phone: "Telefon",
    note: "Notiz",
    total: "Gesamt",
    piece: "Stk.",
    markPreparing: "In Vorbereitung",
    markCompleted: "Abgeschlossen",
    cancel: "Stornieren",
    delete: "Löschen",
    openCash: "Kasse öffnen",
    operationCenter: "Aktionszentrum",
    approveAndPrint: "Bestätigen & Küche drucken",
    preparingOnly: "Nur als in Zubereitung markieren",
    kitchenPrint: "Küchenbon drucken",
    kitchenReprint: "Küchenbon erneut drucken",
    kitchenPrintTitle: "Küchenbon",
    kitchenPrintSubtitle:
      "80 mm ist die Vorgabe für Giant 100D; für andere Drucker kann die Papiergröße gewählt werden.",
    paperSize: "Papier-/Druckergröße",
    paper58: "58 mm Thermo",
    paper80: "80 mm Thermo",
    paperA4: "A4 / Standarddrucker",
    includePrices: "Preise auf dem Bon anzeigen",
    printNow: "Druckdialog öffnen",
    printHelp:
      "Wählen Sie einen im Betriebssystem installierten USB- oder Netzwerkdrucker.",
    popupBlocked:
      "Das Druckfenster konnte nicht geöffnet werden. Prüfen Sie die Pop-up-Berechtigung.",
    printOpened: "Der Druckdialog für den Küchenbon wurde geöffnet.",
    kitchenReceipt: "KÜCHENBESTELLBON",
    orderNo: "Bestellnummer",
    orderTime: "Bestellzeit",
    printedAt: "Druckzeit",
    products: "ARTIKEL",
    noItems: "Keine Artikelinformationen gefunden.",
    preparationNote: "BESTELLNOTIZ",
    noOrders: "Noch keine Bestellungen.",
    noFiltered:
      "Keine Bestellung passt zu den Filtern.",
    noWaiter: "Noch keine Kellnerrufe.",
    guest: "Gast",
    unspecified: "Nicht angegeben",
    markRead: "Als gelesen markieren",
    seen: "Bearbeitet",
    close: "Schließen",
    retry: "Erneut versuchen",
    cancelTitle:
      "Bestellung stornieren?",
    cancelText:
      "Die Bestellung wird als storniert markiert. Anschließend wird die Liste aktualisiert.",
    giveUp: "Zurück",
    statusUpdated:
      "Bestellung #{id} wurde aktualisiert.",
    statusAndCash:
      "Bestellung #{id} abgeschlossen und an die Kasse übertragen.",
    cashAlready:
      "Diese Bestellung wurde bereits an die Kasse übertragen.",
    cashFail:
      "Status aktualisiert, aber Kassenübertragung fehlgeschlagen.",
    cashZero:
      "Betrag ist null; keine Kassenübertragung.",
    newOrderMsg:
      "{count} neue Bestellung erhalten!",
    newWaiterMsg:
      "{count} neuer Kellnerruf!",
    totalOrders: "Bestellungen",
    newOrders: "Neue Bestellungen",
    dailyRevenue: "Tagesumsatz",
    criticalStock: "Kritischer Bestand",
    orderList:
      "Eingehende Bestellungen",
    orderListSub:
      "Tippen Sie auf die Zusammenfassung, um Details zu öffnen.",
    paymentRequests:
      "Tisch-Zahlungsanfragen",
    paymentSub:
      "Zahlung, Rechnungsteilung und Trinkgeld aus dem QR-Menü",
    waitingRequest: "offene Anfrage",
    noPayment:
      "Keine offenen Tisch-Zahlungsanfragen.",
    paymentType: "Zahlungsart",
    tip: "Trinkgeld",
    cashPaid: "Bar bezahlt",
    cardPaid: "Karte bezahlt",
    reject: "Ablehnen",
    stockManagement: "Lagerverwaltung",
    stockHelp:
      "Bestände und Mindestwarnstufen der Menüprodukte verwalten.",
    addStock: "Bestand hinzufügen",
    productName: "Produktname",
    stockQty: "Bestand",
    minStock: "Mindestwarnung",
    save: "Speichern",
    critical: "Kritisch / Leer",
    normal: "Bestand normal",
    noStock:
      "Noch keine Bestände. Mit Bestand hinzufügen neue Datensätze anlegen.",
    stockNameRequired:
      "Produktname darf nicht leer sein.",
    stockSaved:
      "Bestand für {name} gespeichert.",
    stockDeleted: "Bestand gelöscht.",
    stockSaveFail:
      "Bestand konnte nicht gespeichert werden.",
    stockDeleteFail:
      "Bestand konnte nicht gelöscht werden.",
    reports:
      "Verkaufs- und Umsatzberichte",
    reportsSub:
      "Bestellungen, Umsatz und meistverkaufte Produkte verfolgen.",
    daily: "Täglich",
    monthly: "Monatlich",
    period: "Zeitraum",
    orderCount: "Bestellungen",
    revenue: "Umsatz",
    topProducts:
      "Meistverkaufte Produkte",
    noSales:
      "Noch keine Verkaufsdaten.",
    loadingPayments:
      "Zahlungsanfragen werden geladen...",
    newCall: "Neuer Ruf",
    waiterSubtitle:
      "Tischrufe schnell verwalten.",
    waiterEmptySubtitle:
      "Alle Tische wirken derzeit ruhig.",
    touchToClose:
      "Zum Schließen tippen.",
    deleteConfirm:
      "Bestellung #{id} dauerhaft löschen?",
    deleteSuccess:
      "Bestellung #{id} dauerhaft gelöscht.",
    deleteFail:
      "Bestellung konnte nicht gelöscht werden.",
  },
};

function getPanelLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw)
    .slice(0, 2)
    .toLowerCase();

  return ["tr", "en", "de"].includes(lang)
    ? lang
    : "tr";
}

function getLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function normalizeCurrency(value) {
  const currency = String(
    value || "TRY"
  ).toUpperCase();

  return currency === "TL"
    ? "TRY"
    : currency;
}

function moneyText(
  value,
  currency = "TRY",
  lang = "tr"
) {
  const number = Number(value || 0);

  return `${number.toLocaleString(
    getLocale(lang),
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )} ${normalizeCurrency(currency)}`;
}

function normalizePaymentMethod(value) {
  const method = String(
    value || ""
  ).toLowerCase();

  if (
    method.includes("card") ||
    method.includes("kart") ||
    method.includes("online")
  ) {
    return "card";
  }

  if (
    method.includes("bank") ||
    method.includes("banka") ||
    method.includes("havale") ||
    method.includes("eft")
  ) {
    return "bank";
  }

  return "cash";
}

function getOrderCurrency(order) {
  return normalizeCurrency(
    order?.currency ||
      order?.para_birimi ||
      order?.items?.find(
        (item) => item.currency
      )?.currency ||
      "TRY"
  );
}

function getOrderTotal(order) {
  return Number(
    order?.toplam_tutar ||
      order?.total_amount ||
      order?.total ||
      0
  );
}

/*
 * Masa Yönetimi ile aynı masa alanı okuma mantığı.
 * PublicMenu veya backend hangi alanı döndürürse
 * döndürsün masa görünür.
 */
function getTableLabel(record) {
  return String(
    record?.masa_no ||
      record?.table_no ||
      record?.masa ||
      record?.masa_adi ||
      record?.table_name ||
      record?.qr_masa_no ||
      ""
  ).trim();
}

function getItemQty(item) {
  return Number(
    item?.quantity ||
      item?.adet ||
      item?.qty ||
      1
  );
}

function getItemTitle(item) {
  return (
    item?.title ||
    item?.urun_adi ||
    item?.name ||
    item?.ad ||
    "-"
  );
}

function getItemPrice(item) {
  return Number(
    item?.price ||
      item?.unit_price ||
      item?.fiyat ||
      0
  );
}

function formatDateValue(value, lang) {
  if (!value) return "";

  try {
    const raw = String(value).trim();

    const normalized = raw.includes("T")
      ? raw
      : raw.replace(" ", "T");

    const hasTimezone =
      /Z$/i.test(normalized) ||
      /[+-]\d{2}:\d{2}$/.test(
        normalized
      );

    const date = new Date(
      hasTimezone
        ? normalized
        : `${normalized}Z`
    );

    return new Intl.DateTimeFormat(
      getLocale(lang),
      {
        timeZone: "Europe/Istanbul",
        dateStyle: "short",
        timeStyle: "short",
      }
    ).format(date);
  } catch {
    return String(value);
  }
}

function escapePrintHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getKitchenItemDetail(item) {
  const raw =
    item?.options ||
    item?.secenekler ||
    item?.choices ||
    item?.extras ||
    item?.ekstralar ||
    item?.urun_notu ||
    item?.note ||
    item?.not ||
    "";

  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === "string") return entry;

        return (
          entry?.label ||
          entry?.name ||
          entry?.title ||
          entry?.value ||
          ""
        );
      })
      .filter(Boolean)
      .join(", ");
  }

  if (raw && typeof raw === "object") {
    return Object.entries(raw)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
  }

  return String(raw || "").trim();
}

function buildKitchenTicketHtml({
  order,
  paperSize,
  includePrices,
  lang,
  TT,
}) {
  const isA4 = paperSize === "a4";
  const contentWidth =
    paperSize === "58"
      ? "50mm"
      : paperSize === "80"
      ? "72mm"
      : "180mm";

  const fontSize =
    paperSize === "58"
      ? "12px"
      : paperSize === "80"
      ? "14px"
      : "16px";

  const titleSize =
    paperSize === "58"
      ? "18px"
      : paperSize === "80"
      ? "22px"
      : "28px";

  const tableLabel =
    getTableLabel(order) || TT.unspecified;

  const customer =
    order?.musteri_adi ||
    order?.customer_name ||
    TT.guest;

  const currency = getOrderCurrency(order);
  const createdAt = formatDateValue(
    order?.created_at,
    lang
  );

  const printedAt = new Intl.DateTimeFormat(
    getLocale(lang),
    {
      timeZone: "Europe/Istanbul",
      dateStyle: "short",
      timeStyle: "short",
    }
  ).format(new Date());

  const itemRows = (order?.items || [])
    .map((item) => {
      const qty = getItemQty(item);
      const title = getItemTitle(item);
      const detail = getKitchenItemDetail(item);
      const lineTotal = getItemPrice(item) * qty;

      return `
        <div class="item-row">
          <div class="qty">${escapePrintHtml(qty)}x</div>
          <div class="item-content">
            <div class="item-title">${escapePrintHtml(title)}</div>
            ${
              detail
                ? `<div class="item-detail">${escapePrintHtml(detail)}</div>`
                : ""
            }
          </div>
          ${
            includePrices
              ? `<div class="price">${escapePrintHtml(
                  moneyText(lineTotal, item?.currency || currency, lang)
                )}</div>`
              : ""
          }
        </div>
      `;
    })
    .join("");

  const phone =
    order?.telefon || order?.phone || "";

  const orderNote =
    order?.siparis_notu ||
    order?.order_note ||
    order?.note ||
    "";

  return `<!doctype html>
<html lang="${escapePrintHtml(lang)}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapePrintHtml(TT.kitchenPrintTitle)} #${escapePrintHtml(
      order?.id
    )}</title>
    <style>
      @page {
        size: ${isA4 ? "A4 portrait" : "auto"};
        margin: ${isA4 ? "12mm" : "2mm"};
      }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      body {
        width: ${contentWidth};
        max-width: 100%;
        margin: 0 auto;
        font-family: Arial, Helvetica, sans-serif;
        font-size: ${fontSize};
        line-height: 1.3;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .ticket { width: 100%; padding: ${isA4 ? "0" : "1mm"}; }
      .title {
        text-align: center;
        font-size: ${titleSize};
        font-weight: 900;
        padding-bottom: 2mm;
        border-bottom: 2px solid #000;
      }
      .order-number {
        margin: 2mm 0;
        padding: 2mm 1mm;
        border: 2px solid #000;
        text-align: center;
        font-size: ${isA4 ? "26px" : "20px"};
        font-weight: 900;
      }
      .meta { border-bottom: 1px dashed #000; padding-bottom: 2mm; }
      .meta-row { display: flex; justify-content: space-between; gap: 3mm; margin: 1mm 0; }
      .meta-row strong { flex: 0 0 auto; }
      .meta-row span { min-width: 0; text-align: right; overflow-wrap: anywhere; }
      .section-title {
        margin: 2.5mm 0 1mm;
        padding: 1mm 0;
        border-bottom: 2px solid #000;
        font-weight: 900;
        text-align: center;
        letter-spacing: .08em;
      }
      .item-row {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) ${
          includePrices ? "auto" : ""
        };
        gap: 2mm;
        align-items: start;
        padding: 2mm 0;
        border-bottom: 1px dashed #000;
        page-break-inside: avoid;
      }
      .qty { font-size: ${isA4 ? "21px" : "17px"}; font-weight: 900; }
      .item-title { font-size: ${isA4 ? "19px" : "16px"}; font-weight: 900; overflow-wrap: anywhere; }
      .item-detail { margin-top: .8mm; font-size: .9em; font-weight: 700; overflow-wrap: anywhere; }
      .price { white-space: nowrap; font-weight: 900; text-align: right; }
      .note {
        margin-top: 2.5mm;
        padding: 2.5mm;
        border: 3px solid #000;
        font-size: ${isA4 ? "20px" : "16px"};
        font-weight: 900;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      .total {
        margin-top: 2mm;
        padding-top: 2mm;
        border-top: 2px solid #000;
        text-align: right;
        font-size: ${isA4 ? "22px" : "18px"};
        font-weight: 900;
      }
      .footer { margin-top: 3mm; padding-top: 2mm; border-top: 1px dashed #000; text-align: center; font-size: .8em; }
      @media screen {
        body { padding: 10px; }
        .ticket { border: 1px solid #ddd; }
      }
      @media print {
        body { padding: 0; }
        .ticket { border: 0; }
      }
    </style>
  </head>
  <body>
    <main class="ticket">
      <div class="title">${escapePrintHtml(TT.kitchenReceipt)}</div>
      <div class="order-number">#${escapePrintHtml(order?.id)}</div>
      <div class="meta">
        <div class="meta-row"><strong>${escapePrintHtml(
          TT.table
        )}</strong><span>${escapePrintHtml(tableLabel)}</span></div>
        <div class="meta-row"><strong>${escapePrintHtml(
          TT.customer
        )}</strong><span>${escapePrintHtml(customer)}</span></div>
        ${
          phone
            ? `<div class="meta-row"><strong>${escapePrintHtml(
                TT.phone
              )}</strong><span>${escapePrintHtml(phone)}</span></div>`
            : ""
        }
        <div class="meta-row"><strong>${escapePrintHtml(
          TT.orderTime
        )}</strong><span>${escapePrintHtml(createdAt || "-")}</span></div>
      </div>
      <div class="section-title">${escapePrintHtml(TT.products)}</div>
      ${itemRows || `<div class="item-row">${escapePrintHtml(TT.noItems)}</div>`}
      ${
        orderNote
          ? `<div class="note"><div>${escapePrintHtml(
              TT.preparationNote
            )}</div><div>${escapePrintHtml(orderNote)}</div></div>`
          : ""
      }
      ${
        includePrices
          ? `<div class="total">${escapePrintHtml(TT.total)}: ${escapePrintHtml(
              moneyText(getOrderTotal(order), currency, lang)
            )}</div>`
          : ""
      }
      <div class="footer">${escapePrintHtml(TT.printedAt)}: ${escapePrintHtml(
    printedAt
  )}</div>
    </main>
  </body>
</html>`;
}

function openKitchenPrintWindow(options) {
  const printWindow = window.open(
    "",
    "_blank",
    "width=520,height=760"
  );

  if (!printWindow) return false;

  printWindow.document.open();
  printWindow.document.write(buildKitchenTicketHtml(options));
  printWindow.document.close();
  printWindow.opener = null;

  const triggerPrint = () => {
    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      // Yazdırma penceresi açık kalır; kullanıcı tarayıcı menüsünden yazdırabilir.
    }
  };

  if (printWindow.document.readyState === "complete") {
    window.setTimeout(triggerPrint, 250);
  } else {
    printWindow.addEventListener("load", () => {
      window.setTimeout(triggerPrint, 250);
    });
  }

  return true;
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(22px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%,
  100% {
    transform: scale(1);
    opacity: 0.82;
  }

  50% {
    transform: scale(1.045);
    opacity: 1;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const alertPop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.78);
  }

  60% {
    opacity: 1;
    transform: scale(1.04);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const ring = keyframes`
  0% {
    transform: scale(0.75);
    opacity: 0.7;
  }

  80%,
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
`;

function useOrders(
  token,
  pollIntervalMs = 15000
) {
  const [orders, setOrders] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const fetchOrders =
    useCallback(async () => {
      if (!token) {
        throw new Error(
          "Token eksik"
        );
      }

      const response =
        await fetch(
          `${API_BASE}/qr-orders`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      const json =
        await response.json();

      return json.data || [];
    }, [token]);

  const loadOrders =
    useCallback(async () => {
      if (!token) return;

      setError(null);

      try {
        const rows =
          await fetchOrders();

        setOrders(rows);
      } catch (loadError) {
        setError(
          loadError.message ||
            "Siparişler alınamadı"
        );
      } finally {
        setLoading(false);
      }
    }, [fetchOrders, token]);

  useEffect(() => {
    loadOrders();

    const timer = setInterval(
      loadOrders,
      pollIntervalMs
    );

    return () =>
      clearInterval(timer);
  }, [loadOrders, pollIntervalMs]);

  const updateOrderStatus =
    useCallback(
      async (
        orderId,
        newStatus
      ) => {
        if (!token) {
          throw new Error(
            "Token eksik"
          );
        }

        const previousOrders = [
          ...orders,
        ];

        setOrders((current) =>
          current.map((order) =>
            String(order.id) ===
            String(orderId)
              ? {
                  ...order,
                  durum:
                    newStatus,
                }
              : order
          )
        );

        try {
          const response =
            await fetch(
              `${API_BASE}/qr-orders/${orderId}/status`,
              {
                method: "PUT",

                headers: {
                  "Content-Type":
                    "application/json",

                  Authorization:
                    `Bearer ${token}`,
                },

                body:
                  JSON.stringify({
                    durum:
                      newStatus,
                  }),
              }
            );

          if (!response.ok) {
            throw new Error(
              "Durum güncellenemedi"
            );
          }

          await loadOrders();
        } catch (updateError) {
          setOrders(
            previousOrders
          );

          throw updateError;
        }
      },
      [
        loadOrders,
        orders,
        token,
      ]
    );

  return {
    orders,
    loading,
    error,
    updateOrderStatus,
    refetch: loadOrders,
  };
}

function CenterAlert({
  data,
  onClose,
  TT,
}) {
  if (!data.open) return null;

  const isWaiter =
    data.type === "waiter";

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        p: 2,

        bgcolor:
          "rgba(15,23,42,.48)",

        backdropFilter:
          "blur(8px)",
      }}
    >
      <Box
        sx={{
          position: "relative",

          width: "100%",
          maxWidth: 520,

          overflow: "hidden",

          borderRadius: {
            xs: 4,
            md: 5,
          },

          p: {
            xs: 3,
            md: 4.5,
          },

          textAlign: "center",

          bgcolor: "#fff",

          background: isWaiter
            ? "linear-gradient(145deg,#fff7ed,#ffffff 58%,#ffedd5)"
            : "linear-gradient(145deg,#eff6ff,#ffffff 58%,#dbeafe)",

          border:
            `2px solid ${
              isWaiter
                ? "rgba(249,115,22,.34)"
                : "rgba(37,99,235,.25)"
            }`,

          boxShadow:
            "0 34px 90px rgba(15,23,42,.3)",

          animation:
            `${alertPop} .42s ease-out`,
        }}
      >
        <Box
          sx={{
            position: "absolute",

            width: 94,
            height: 94,

            right: 22,
            top: 18,

            borderRadius:
              "50%",

            bgcolor:
              isWaiter
                ? "rgba(249,115,22,.12)"
                : "rgba(37,99,235,.12)",

            animation:
              `${ring} 1.8s ease-out infinite`,
          }}
        />

        <Avatar
          sx={{
            width: {
              xs: 92,
              md: 112,
            },

            height: {
              xs: 92,
              md: 112,
            },

            mx: "auto",
            mb: 2,

            fontSize: {
              xs: 44,
              md: 56,
            },

            background:
              isWaiter
                ? "linear-gradient(135deg,#f97316,#f59e0b)"
                : "linear-gradient(135deg,#0b4d8b,#2563eb)",

            boxShadow:
              isWaiter
                ? "0 18px 42px rgba(249,115,22,.35)"
                : "0 18px 42px rgba(37,99,235,.32)",

            animation:
              `${pulse} 1.1s ease-in-out infinite`,
          }}
        >
          {data.icon}
        </Avatar>

        <Typography
          sx={{
            fontWeight: 950,

            fontSize: {
              xs: 24,
              sm: 30,
              md: 36,
            },

            lineHeight: 1.1,

            color:
              BRAND.navy,

            letterSpacing: -0.8,
          }}
        >
          {data.message}
        </Typography>

        <Typography
          sx={{
            mt: 1,

            fontWeight: 900,

            color: isWaiter
              ? BRAND.orangeDark
              : BRAND.blueDark,

            fontSize: {
              xs: 15,
              md: 18,
            },
          }}
        >
          {data.title}
        </Typography>

        {data.subtitle && (
          <Typography
            sx={{
              mt: 1,

              color:
                "text.secondary",

              fontWeight: 750,
            }}
          >
            {data.subtitle}
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{
            mt: 2,

            display: "block",

            color:
              "text.secondary",
          }}
        >
          {TT.touchToClose}
        </Typography>
      </Box>
    </Box>
  );
}

function OrderAccordion({
  order,
  TT,
  lang,
  onStatusChange,
  onKitchenPrint,
  onCancel,
  onDelete,
}) {
  const theme = useTheme();

  const [updating, setUpdating] =
    useState(false);

  const [expanded, setExpanded] =
    useState(
      order.durum === "yeni"
    );

  useEffect(() => {
    if (
      order.durum === "yeni"
    ) {
      setExpanded(true);
    }
  }, [order.durum]);

  const statusMeta =
    {
      yeni: {
        label: TT.new,
        color: "warning",
        main:
          theme.palette.warning
            .main,
        icon: (
          <NewReleasesIcon fontSize="small" />
        ),
      },

      hazirlaniyor: {
        label: TT.preparing,
        color: "info",
        main:
          theme.palette.info.main,
        icon: (
          <LocalDiningIcon fontSize="small" />
        ),
      },

      tamamlandi: {
        label: TT.completed,
        color: "success",
        main:
          theme.palette.success
            .main,
        icon: (
          <CheckCircleIcon fontSize="small" />
        ),
      },

      iptal: {
        label: TT.cancelled,
        color: "error",
        main:
          theme.palette.error
            .main,
        icon: (
          <CancelIcon fontSize="small" />
        ),
      },
    }[order.durum] || {
      label:
        order.durum || "-",

      color: "default",

      main:
        theme.palette.grey[500],

      icon: (
        <ReceiptLongIcon fontSize="small" />
      ),
    };

  const currency =
    getOrderCurrency(order);

  const total =
    getOrderTotal(order);

  const tableLabel =
    getTableLabel(order) ||
    TT.unspecified;

  const itemCount = (
    order.items || []
  ).reduce(
    (sum, item) =>
      sum + getItemQty(item),
    0
  );

  const runAction =
    async (status) => {
      if (updating) return;

      setUpdating(true);

      try {
        await onStatusChange(
          order.id,
          status
        );
      } finally {
        setUpdating(false);
      }
    };

  const runApproveAndPrint = async () => {
    if (updating) return;

    setUpdating(true);

    try {
      let statusOk = true;

      if (order.durum === "yeni") {
        statusOk = await onStatusChange(
          order.id,
          "hazirlaniyor"
        );
      }

      if (statusOk !== false) {
        onKitchenPrint(order);
      }
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(
        _,
        value
      ) => setExpanded(value)}
      disableGutters
      elevation={0}
      sx={{
        width: "100%",
        minWidth: 0,

        overflow: "hidden",

        borderRadius:
          "20px !important",

        border:
          `1px solid ${alpha(
            statusMeta.main,
            0.24
          )}`,

        borderLeft:
          `7px solid ${statusMeta.main}`,

        background:
          order.durum === "yeni"
            ? `linear-gradient(
                135deg,
                ${alpha(
                  theme.palette
                    .warning.main,
                  0.08
                )},
                #fff 42%
              )`
            : "linear-gradient(145deg,#fff,#f8fafc)",

        boxShadow: expanded
          ? `0 24px 58px ${alpha(
              statusMeta.main,
              0.16
            )}`
          : "0 12px 32px rgba(15,23,42,.07)",

        animation:
          `${fadeInUp} .32s ease both`,

        transition:
          "transform .22s ease, box-shadow .22s ease",

        "&:before": {
          display: "none",
        },

        "&.Mui-expanded": {
          m: 0,
        },

        "&:hover": {
          transform: {
            xs: "none",
            md: "translateY(-2px)",
          },

          boxShadow:
            `0 22px 52px ${alpha(
              statusMeta.main,
              0.15
            )}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Avatar
            sx={{
              width: 38,
              height: 38,

              bgcolor: alpha(
                statusMeta.main,
                0.11
              ),

              color:
                statusMeta.main,
            }}
          >
            <ExpandMoreIcon />
          </Avatar>
        }
        sx={{
          px: {
            xs: 1.25,
            sm: 2,
            md: 2.4,
          },

          py: {
            xs: 0.8,
            sm: 1,
          },

          minHeight: {
            xs: 112,
            sm: 94,
          },

          "& .MuiAccordionSummary-content":
            {
              minWidth: 0,
              my: 0.8,
            },

          "& .MuiAccordionSummary-expandIconWrapper":
            {
              ml: {
                xs: 0.5,
                sm: 1,
              },
            },
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth: 0,

            display: "grid",

            gridTemplateColumns: {
              xs: "minmax(0,1fr)",
              sm: "auto minmax(180px,1fr) auto",
            },

            alignItems: "center",

            gap: {
              xs: 1,
              sm: 1.4,
              md: 2,
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1.1}
            alignItems="center"
            sx={{
              minWidth: 0,
            }}
          >
            <Avatar
              sx={{
                width: {
                  xs: 45,
                  sm: 52,
                },

                height: {
                  xs: 45,
                  sm: 52,
                },

                bgcolor: alpha(
                  statusMeta.main,
                  0.13
                ),

                color:
                  statusMeta.main,

                border:
                  `1px solid ${alpha(
                    statusMeta.main,
                    0.22
                  )}`,

                fontSize: 14,
                fontWeight: 950,

                flexShrink: 0,
              }}
            >
              #{order.id}
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  sx={{
                    color:
                      BRAND.navy,

                    fontWeight: 950,

                    fontSize: {
                      xs: 16,
                      sm: 18,
                    },
                  }}
                >
                  {TT.table}:{" "}
                  {tableLabel}
                </Typography>

                <Chip
                  size="small"
                  icon={
                    statusMeta.icon
                  }
                  label={
                    statusMeta.label
                  }
                  color={
                    statusMeta.color
                  }
                  sx={{
                    height: 27,

                    borderRadius: 999,

                    fontWeight: 900,

                    ...(order.durum ===
                      "yeni" && {
                      animation:
                        `${pulse} 1.5s ease-in-out infinite`,
                    }),
                  }}
                />
              </Stack>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                sx={{
                  mt: 0.5,
                  minWidth: 0,
                }}
              >
                <AccessTimeIcon
                  sx={{
                    fontSize: 16,

                    color:
                      "text.secondary",

                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    color:
                      "text.secondary",

                    fontWeight: 750,

                    overflow: "hidden",

                    textOverflow:
                      "ellipsis",

                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {formatDateValue(
                    order.created_at,
                    lang
                  )}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Box
            sx={{
              minWidth: 0,

              display: "grid",

              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",
                md: "repeat(3,minmax(0,1fr))",
              },

              gap: 0.8,
            }}
          >
            <Stack
              direction="row"
              spacing={0.7}
              alignItems="center"
              sx={{
                minWidth: 0,

                px: 1,
                py: 0.8,

                borderRadius: 2.5,

                bgcolor: alpha(
                  theme.palette.primary
                    .main,
                  0.055
                ),

                border:
                  `1px solid ${alpha(
                    theme.palette
                      .primary.main,
                    0.11
                  )}`,
              }}
            >
              <PersonOutlineIcon
                sx={{
                  fontSize: 18,

                  color:
                    "primary.main",

                  flexShrink: 0,
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,

                  overflow: "hidden",

                  textOverflow:
                    "ellipsis",

                  whiteSpace:
                    "nowrap",

                  fontWeight: 850,
                }}
              >
                {order.musteri_adi ||
                  TT.guest}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.7}
              alignItems="center"
              sx={{
                px: 1,
                py: 0.8,

                borderRadius: 2.5,

                bgcolor: alpha(
                  theme.palette.primary
                    .main,
                  0.055
                ),

                border:
                  `1px solid ${alpha(
                    theme.palette
                      .primary.main,
                    0.11
                  )}`,
              }}
            >
              <ReceiptLongIcon
                sx={{
                  fontSize: 18,

                  color:
                    "primary.main",
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 850,
                }}
              >
                {itemCount}{" "}
                {TT.piece}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.7}
              alignItems="center"
              sx={{
                display: {
                  xs: "none",
                  md: "flex",
                },

                minWidth: 0,

                px: 1,
                py: 0.8,

                borderRadius: 2.5,

                bgcolor: alpha(
                  theme.palette.primary
                    .main,
                  0.055
                ),

                border:
                  `1px solid ${alpha(
                    theme.palette
                      .primary.main,
                    0.11
                  )}`,
              }}
            >
              <PhoneOutlinedIcon
                sx={{
                  fontSize: 18,

                  color:
                    "primary.main",

                  flexShrink: 0,
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,

                  overflow: "hidden",

                  textOverflow:
                    "ellipsis",

                  whiteSpace:
                    "nowrap",

                  fontWeight: 850,
                }}
              >
                {order.telefon ||
                  "-"}
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              minWidth: {
                sm: 130,
              },

              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color:
                  "text.secondary",

                fontWeight: 800,
              }}
            >
              {TT.total}
            </Typography>

            <Typography
              sx={{
                color:
                  "success.dark",

                fontWeight: 950,

                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1.1,

                whiteSpace:
                  "nowrap",
              }}
            >
              {moneyText(
                total,
                currency,
                lang
              )}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: {
            xs: 1.25,
            sm: 2,
            md: 2.4,
          },

          pt: 0,

          pb: {
            xs: 1.5,
            sm: 2.2,
          },
        }}
      >
        <Divider
          sx={{
            mb: 1.5,

            borderStyle:
              "dashed",
          }}
        />

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0,1fr) 310px",
            },

            gap: {
              xs: 1.5,
              lg: 2,
            },

            alignItems: "start",
          }}
        >
          <Stack
            spacing={1}
            sx={{
              minWidth: 0,
            }}
          >
            {(order.items || []).map(
              (item, index) => {
                const qty =
                  getItemQty(item);

                const title =
                  getItemTitle(item);

                const price =
                  getItemPrice(item);

                return (
                  <Box
                    key={`${order.id}-${index}`}
                    sx={{
                      display:
                        "grid",

                      gridTemplateColumns:
                        "auto minmax(0,1fr) auto",

                      alignItems:
                        "center",

                      gap: 1,

                      p: {
                        xs: 1,
                        sm: 1.2,
                      },

                      borderRadius: 3,

                      bgcolor: alpha(
                        theme.palette
                          .grey[500],
                        0.045
                      ),

                      border:
                        `1px solid ${alpha(
                          theme.palette
                            .grey[500],
                          0.1
                        )}`,
                    }}
                  >
                    <Chip
                      size="small"
                      label={`${qty}x`}
                      sx={{
                        bgcolor:
                          alpha(
                            statusMeta.main,
                            0.12
                          ),

                        color:
                          statusMeta.main,

                        fontWeight:
                          950,
                      }}
                    />

                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color:
                            BRAND.navy,

                          fontWeight:
                            900,

                          overflowWrap:
                            "anywhere",
                        }}
                      >
                        {title}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            "text.secondary",

                          fontWeight:
                            750,
                        }}
                      >
                        {moneyText(
                          price,
                          item.currency ||
                            currency,
                          lang
                        )}{" "}
                        × {qty}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        color:
                          BRAND.blueDark,

                        fontWeight: 950,

                        whiteSpace:
                          "nowrap",

                        fontSize: {
                          xs: 13,
                          sm: 14,
                        },
                      }}
                    >
                      {moneyText(
                        price * qty,
                        item.currency ||
                          currency,
                        lang
                      )}
                    </Typography>
                  </Box>
                );
              }
            )}

            {order.siparis_notu && (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 3,

                  border:
                    `1px solid ${alpha(
                      theme.palette
                        .warning.main,
                      0.2
                    )}`,

                  "& .MuiAlert-message":
                    {
                      minWidth: 0,

                      overflowWrap:
                        "anywhere",
                    },
                }}
              >
                <b>{TT.note}:</b>{" "}
                {order.siparis_notu}
              </Alert>
            )}

            {order.telefon && (
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                sx={{
                  display: {
                    xs: "flex",
                    md: "none",
                  },

                  px: 1.1,
                  py: 0.9,

                  borderRadius: 2.5,

                  bgcolor: alpha(
                    theme.palette
                      .primary.main,
                    0.055
                  ),

                  border:
                    `1px solid ${alpha(
                      theme.palette
                        .primary.main,
                      0.11
                    )}`,
                }}
              >
                <PhoneOutlinedIcon
                  sx={{
                    fontSize: 18,

                    color:
                      "primary.main",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 850,
                  }}
                >
                  {order.telefon}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Card
            variant="outlined"
            sx={{
              p: {
                xs: 1.25,
                sm: 1.5,
              },

              borderRadius: 3.5,

              borderColor:
                alpha(
                  statusMeta.main,
                  0.18
                ),

              boxShadow:
                "0 12px 32px rgba(15,23,42,.06)",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1.2,
              }}
            >
              <Typography
                sx={{
                  color:
                    BRAND.navy,

                  fontWeight: 950,
                }}
              >
                {TT.operationCenter}
              </Typography>

              {updating && (
                <CircularProgress
                  size={20}
                />
              )}
            </Stack>

            <Stack spacing={1}>
              {order.durum !== "iptal" && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PrintIcon />}
                  disabled={updating}
                  onClick={runApproveAndPrint}
                  sx={{
                    minHeight: 52,
                    borderRadius: 2.7,
                    textTransform: "none",
                    fontWeight: 950,
                    color: "#fff",
                    bgcolor: BRAND.orange,
                    boxShadow: "0 10px 24px rgba(249,115,22,.24)",
                    "&:hover": {
                      bgcolor: BRAND.orangeDark,
                    },
                  }}
                >
                  {order.durum === "yeni"
                    ? TT.approveAndPrint
                    : TT.kitchenReprint}
                </Button>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={
                  <LocalDiningIcon />
                }
                disabled={
                  updating ||
                  order.durum ===
                    "hazirlaniyor" ||
                  order.durum ===
                    "tamamlandi" ||
                  order.durum ===
                    "iptal"
                }
                onClick={() =>
                  runAction(
                    "hazirlaniyor"
                  )
                }
                sx={{
                  minHeight: 48,

                  borderRadius: 2.7,

                  textTransform:
                    "none",

                  fontWeight: 950,
                }}
              >
                {TT.preparingOnly}
              </Button>

              <Button
                fullWidth
                color="success"
                variant="contained"
                startIcon={
                  <CheckCircleIcon />
                }
                disabled={
                  updating ||
                  order.durum ===
                    "tamamlandi" ||
                  order.durum ===
                    "iptal"
                }
                onClick={() =>
                  runAction(
                    "tamamlandi"
                  )
                }
                sx={{
                  minHeight: 48,

                  borderRadius: 2.7,

                  textTransform:
                    "none",

                  fontWeight: 950,
                }}
              >
                {TT.markCompleted}
              </Button>

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr 1fr",
                      lg: "1fr",
                    },

                  gap: 1,
                }}
              >
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  startIcon={
                    <CancelIcon />
                  }
                  disabled={
                    updating ||
                    order.durum ===
                      "iptal"
                  }
                  onClick={() =>
                    onCancel(
                      order.id
                    )
                  }
                  sx={{
                    minHeight: 46,

                    borderRadius:
                      2.7,

                    textTransform:
                      "none",

                    fontWeight: 900,

                    borderWidth: 2,

                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  {TT.cancel}
                </Button>

                <Button
                  fullWidth
                  color="error"
                  variant="contained"
                  startIcon={
                    <DeleteIcon />
                  }
                  disabled={updating}
                  onClick={() =>
                    onDelete(
                      order.id
                    )
                  }
                  sx={{
                    minHeight: 46,

                    borderRadius:
                      2.7,

                    textTransform:
                      "none",

                    fontWeight: 900,

                    bgcolor:
                      "#991b1b",

                    "&:hover": {
                      bgcolor:
                        "#7f1d1d",
                    },
                  }}
                >
                  {TT.delete}
                </Button>
              </Box>

              {order.durum ===
                "tamamlandi" && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <PaymentsOutlinedIcon />
                  }
                  onClick={() => {
                    window.location.hash =
                      "/kasa-tahsilat";
                  }}
                  sx={{
                    minHeight: 48,

                    borderRadius:
                      2.7,

                    textTransform:
                      "none",

                    fontWeight: 950,

                    borderWidth: 2,

                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  {TT.openCash}
                </Button>
              )}
            </Stack>
          </Card>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function PaymentRequestsPanel({
  token,
  lang,
  TT,
}) {
  const theme = useTheme();

  const [requests, setRequests] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [updatingId, setUpdatingId] =
    useState(null);

  const loadRequests =
    useCallback(async () => {
      if (!token) return;

      setLoading(true);

      try {
        const response =
          await fetch(
            `${API_BASE}/masa-odeme-talepleri?status=all`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          json?.ok === false
        ) {
          throw new Error(
            json?.message ||
              "Ödeme talepleri alınamadı"
          );
        }

        setRequests(
          json.data || []
        );
      } catch (error) {
        console.error(
          "Ödeme talepleri alınamadı:",
          error
        );
      } finally {
        setLoading(false);
      }
    }, [token]);

  useEffect(() => {
    loadRequests();

    const timer = setInterval(
      loadRequests,
      10000
    );

    return () =>
      clearInterval(timer);
  }, [loadRequests]);

  const updateRequest = async (
    id,
    status,
    paymentMethod = null
  ) => {
    if (!token || updatingId) {
      return;
    }

    setUpdatingId(id);

    try {
      const response =
        await fetch(
          `${API_BASE}/masa-odeme-talepleri/${id}/status`,
          {
            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,
            },

            body:
              JSON.stringify({
                status,

                payment_method:
                  paymentMethod,
              }),
          }
        );

      const json =
        await response
          .json()
          .catch(() => ({}));

      if (
        !response.ok ||
        json?.ok === false
      ) {
        throw new Error(
          json?.message ||
            "Ödeme talebi güncellenemedi"
        );
      }

      await loadRequests();
    } catch (error) {
      console.error(
        "Ödeme talebi güncellenemedi:",
        error
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const pendingCount =
    requests.filter(
      (request) =>
        request.status ===
        "bekliyor"
    ).length;

  return (
    <Card
      sx={{
        mb: {
          xs: 1.5,
          md: 2.5,
        },

        overflow: "hidden",

        borderRadius: {
          xs: 3.5,
          md: 5,
        },

        border:
          `1px solid ${alpha(
            theme.palette.warning
              .main,
            0.18
          )}`,

        background:
          "linear-gradient(145deg,#fff,#fffaf3)",

        boxShadow:
          "0 20px 52px rgba(15,23,42,.08)",

        animation:
          `${fadeInUp} .35s ease both`,
      }}
    >
      <Box
        sx={{
          height: 5,

          background:
            "linear-gradient(90deg,#f97316,#f59e0b,#2563eb)",
        }}
      />

      <CardContent
        sx={{
          p: {
            xs: 1.4,
            sm: 2,
            md: 2.6,
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          spacing={1.2}
          sx={{
            mb: 1.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              minWidth: 0,
            }}
          >
            <Badge
              badgeContent={
                pendingCount
              }
              color="error"
              max={99}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,

                  background:
                    "linear-gradient(135deg,#f97316,#f59e0b)",

                  boxShadow:
                    "0 10px 24px rgba(249,115,22,.26)",
                }}
              >
                <PaymentsOutlinedIcon />
              </Avatar>
            </Badge>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color:
                    BRAND.navy,

                  fontWeight: 950,

                  fontSize: {
                    xs: 18,
                    md: 21,
                  },
                }}
              >
                {TT.paymentRequests}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color:
                    "text.secondary",

                  fontWeight: 700,
                }}
              >
                {TT.paymentSub}
              </Typography>
            </Box>
          </Stack>

          <Chip
            color={
              pendingCount > 0
                ? "warning"
                : "success"
            }
            label={`${pendingCount} ${TT.waitingRequest}`}
            sx={{
              alignSelf: {
                xs: "flex-start",
                sm: "center",
              },

              borderRadius: 999,

              fontWeight: 950,
            }}
          />
        </Stack>

        {loading &&
        requests.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 3,
            }}
          >
            {TT.loadingPayments}
          </Alert>
        ) : requests.length === 0 ? (
          <Alert
            severity="success"
            sx={{
              borderRadius: 3,
            }}
          >
            {TT.noPayment}
          </Alert>
        ) : (
          <Stack spacing={1}>
            {requests
              .slice(0, 12)
              .map((request) => {
                const pending =
                  request.status ===
                  "bekliyor";

                const paid = [
                  "odendi",
                  "onaylandi",
                  "onaylandı",
                ].includes(
                  request.status
                );

                const tableLabel =
                  getTableLabel(
                    request
                  ) ||
                  TT.unspecified;

                const typeText =
                  {
                    full:
                      "Hesabın tamamı",

                    items:
                      "Ürün seçerek ödeme",

                    amount:
                      "Tutar girerek ödeme",

                    people:
                      "Kişi sayısına bölme",
                  }[
                    request
                      .payment_type
                  ] ||
                  request.payment_type;

                return (
                  <Accordion
                    key={request.id}
                    disableGutters
                    elevation={0}
                    defaultExpanded={
                      pending
                    }
                    sx={{
                      overflow:
                        "hidden",

                      borderRadius:
                        "16px !important",

                      border:
                        `1px solid ${
                          pending
                            ? alpha(
                                theme
                                  .palette
                                  .warning
                                  .main,
                                0.25
                              )
                            : alpha(
                                theme
                                  .palette
                                  .grey[500],
                                0.13
                              )
                        }`,

                      bgcolor:
                        pending
                          ? alpha(
                              theme
                                .palette
                                .warning
                                .main,
                              0.045
                            )
                          : "#fff",

                      "&:before": {
                        display:
                          "none",
                      },

                      "&.Mui-expanded":
                        {
                          m: 0,
                        },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon />
                      }
                      sx={{
                        minHeight:
                          72,

                        px: {
                          xs: 1.2,
                          sm: 1.6,
                        },

                        "& .MuiAccordionSummary-content":
                          {
                            minWidth:
                              0,

                            my: 1,
                          },
                      }}
                    >
                      <Box
                        sx={{
                          width:
                            "100%",

                          minWidth: 0,

                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs: "minmax(0,1fr)",

                              sm: "minmax(0,1fr) auto",
                            },

                          alignItems:
                            "center",

                          gap: 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,

                              bgcolor:
                                pending
                                  ? alpha(
                                      theme
                                        .palette
                                        .warning
                                        .main,
                                      0.15
                                    )
                                  : alpha(
                                      theme
                                        .palette
                                        .success
                                        .main,
                                      0.13
                                    ),

                              color:
                                pending
                                  ? "warning.dark"
                                  : "success.dark",
                            }}
                          >
                            <TableRestaurantIcon fontSize="small" />
                          </Avatar>

                          <Box
                            sx={{
                              minWidth:
                                0,
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  BRAND.navy,

                                fontWeight:
                                  950,
                              }}
                            >
                              {TT.table}{" "}
                              {tableLabel}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                display:
                                  "block",

                                color:
                                  "text.secondary",

                                fontWeight:
                                  750,

                                overflow:
                                  "hidden",

                                textOverflow:
                                  "ellipsis",

                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {request.musteri_adi ||
                                TT.guest}{" "}
                              · {typeText}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent={{
                            xs: "space-between",

                            sm: "flex-end",
                          }}
                        >
                          <Chip
                            size="small"
                            color={
                              pending
                                ? "warning"
                                : paid
                                ? "success"
                                : "default"
                            }
                            label={
                              request.status
                            }
                            sx={{
                              fontWeight:
                                900,
                            }}
                          />

                          <Typography
                            sx={{
                              color:
                                BRAND.navy,

                              fontWeight:
                                950,

                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {moneyText(
                              request.amount,
                              "TRY",
                              lang
                            )}
                          </Typography>
                        </Stack>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        px: {
                          xs: 1.2,
                          sm: 1.6,
                        },

                        pt: 0,
                        pb: 1.5,
                      }}
                    >
                      <Divider
                        sx={{
                          mb: 1.2,

                          borderStyle:
                            "dashed",
                        }}
                      />

                      <Box
                        sx={{
                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs: "1fr",

                              md: "minmax(0,1fr) auto",
                            },

                          gap: 1.2,

                          alignItems:
                            "center",
                        }}
                      >
                        <Stack
                          spacing={0.4}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight:
                                850,
                            }}
                          >
                            {
                              TT.paymentType
                            }
                            : {typeText}
                          </Typography>

                          {Number(
                            request.tip_amount ||
                              0
                          ) > 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  "warning.dark",

                                fontWeight:
                                  850,
                              }}
                            >
                              {TT.tip}:{" "}
                              {moneyText(
                                request.tip_amount,
                                "TRY",
                                lang
                              )}
                            </Typography>
                          )}
                        </Stack>

                        {pending && (
                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm: "repeat(3,minmax(120px,1fr))",
                                },

                              gap: 0.8,

                              minWidth: {
                                md: 420,
                              },
                            }}
                          >
                            <Button
                              fullWidth
                              color="success"
                              variant="contained"
                              disabled={
                                updatingId ===
                                request.id
                              }
                              onClick={() =>
                                updateRequest(
                                  request.id,
                                  "odendi",
                                  "cash"
                                )
                              }
                              sx={{
                                minHeight:
                                  44,

                                borderRadius:
                                  2.5,

                                textTransform:
                                  "none",

                                fontWeight:
                                  950,
                              }}
                            >
                              {TT.cashPaid}
                            </Button>

                            <Button
                              fullWidth
                              color="primary"
                              variant="contained"
                              disabled={
                                updatingId ===
                                request.id
                              }
                              onClick={() =>
                                updateRequest(
                                  request.id,
                                  "odendi",
                                  "card"
                                )
                              }
                              sx={{
                                minHeight:
                                  44,

                                borderRadius:
                                  2.5,

                                textTransform:
                                  "none",

                                fontWeight:
                                  950,
                              }}
                            >
                              {TT.cardPaid}
                            </Button>

                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              disabled={
                                updatingId ===
                                request.id
                              }
                              onClick={() =>
                                updateRequest(
                                  request.id,
                                  "reddedildi"
                                )
                              }
                              sx={{
                                minHeight:
                                  44,

                                borderRadius:
                                  2.5,

                                textTransform:
                                  "none",

                                fontWeight:
                                  950,

                                borderWidth:
                                  2,
                              }}
                            >
                              {TT.reject}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function StockPanel({
  stocks,
  setStocks,
  loading,
  onSave,
  onDelete,
  TT,
}) {
  const theme = useTheme();

  const addRecord = () => {
    setStocks((current) => [
      {
        id: `new-${Date.now()}`,

        title: "",
        urun_adi: "",

        stok_adedi: 0,

        minimum_stok: 0,

        manuel: true,
      },

      ...current,
    ]);
  };

  const updateRecord = (
    id,
    field,
    value
  ) => {
    setStocks((current) =>
      current.map((item) =>
        String(item.id) ===
        String(id)
          ? {
              ...item,

              [field]: value,
            }
          : item
      )
    );
  };

  return (
    <Card
      sx={{
        mb: {
          xs: 1.5,
          md: 3,
        },

        overflow: "hidden",

        borderRadius: {
          xs: 3.5,
          md: 5,
        },

        border:
          "1px solid rgba(226,232,240,.9)",

        boxShadow:
          "0 18px 46px rgba(15,23,42,.075)",

        background:
          "linear-gradient(145deg,#fff,#f8fafc)",
      }}
    >
      <Box
        sx={{
          height: 5,

          background:
            "linear-gradient(90deg,#2563eb,#0b4d8b,#f97316)",
        }}
      />

      <CardContent
        sx={{
          p: {
            xs: 1.4,
            sm: 2,
            md: 2.6,
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          spacing={1.4}
          sx={{
            mb: 1.7,
          }}
        >
          <Stack
            direction="row"
            spacing={1.1}
            alignItems="center"
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,

                bgcolor: alpha(
                  theme.palette.primary
                    .main,
                  0.11
                ),

                color:
                  "primary.main",
              }}
            >
              <InventoryIcon />
            </Avatar>

            <Box>
              <Typography
                sx={{
                  color:
                    BRAND.navy,

                  fontWeight: 950,

                  fontSize: {
                    xs: 18,
                    md: 21,
                  },
                }}
              >
                {TT.stockManagement}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color:
                    "text.secondary",

                  fontWeight: 700,
                }}
              >
                {TT.stockHelp}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <AddIcon />
              )
            }
            onClick={addRecord}
            sx={{
              minHeight: 48,

              borderRadius: 2.8,

              textTransform: "none",

              fontWeight: 950,

              px: 2.4,

              background:
                "linear-gradient(135deg,#2563eb,#0b4d8b)",
            }}
          >
            {TT.addStock}
          </Button>
        </Stack>

        {stocks.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 3,
            }}
          >
            {TT.noStock}
          </Alert>
        ) : (
          <Stack spacing={1.1}>
            {stocks.map((item) => {
              const stockQty =
                Number(
                  item.stok_adedi ||
                    0
                );

              const minimum =
                Number(
                  item.minimum_stok ||
                    0
                );

              const critical =
                stockQty <= minimum;

              return (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    overflow:
                      "hidden",

                    borderRadius:
                      3.5,

                    borderColor:
                      critical
                        ? alpha(
                            theme
                              .palette
                              .error
                              .main,
                            0.32
                          )
                        : alpha(
                            theme
                              .palette
                              .success
                              .main,
                            0.22
                          ),

                    bgcolor:
                      critical
                        ? alpha(
                            theme
                              .palette
                              .error
                              .main,
                            0.035
                          )
                        : alpha(
                            theme
                              .palette
                              .success
                              .main,
                            0.025
                          ),
                  }}
                >
                  <Box
                    sx={{
                      p: {
                        xs: 1.2,
                        sm: 1.5,
                      },
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        md: "row",
                      }}
                      spacing={1.2}
                      alignItems={{
                        xs: "stretch",
                        md: "center",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          minWidth: {
                            md: 210,
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 42,
                            height: 42,

                            bgcolor:
                              critical
                                ? alpha(
                                    theme
                                      .palette
                                      .error
                                      .main,
                                    0.12
                                  )
                                : alpha(
                                    theme
                                      .palette
                                      .success
                                      .main,
                                    0.12
                                  ),

                            color:
                              critical
                                ? "error.main"
                                : "success.main",
                          }}
                        >
                          {critical ? (
                            <WarningAmberIcon />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </Avatar>

                        <Box
                          sx={{
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                BRAND.navy,

                              fontWeight:
                                950,

                              overflow:
                                "hidden",

                              textOverflow:
                                "ellipsis",

                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {item.title ||
                              item.urun_adi ||
                              TT.productName}
                          </Typography>

                          <Chip
                            size="small"
                            color={
                              critical
                                ? "error"
                                : "success"
                            }
                            label={
                              critical
                                ? TT.critical
                                : TT.normal
                            }
                            sx={{
                              mt: 0.4,

                              height: 24,

                              fontWeight:
                                900,
                            }}
                          />
                        </Box>
                      </Stack>

                      <Box
                        sx={{
                          flex: 1,

                          minWidth: 0,

                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs: "1fr",

                              sm: "minmax(180px,2fr) repeat(2,minmax(110px,1fr))",
                            },

                          gap: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          label={
                            TT.productName
                          }
                          value={
                            item.title ||
                            item.urun_adi ||
                            ""
                          }
                          onChange={(
                            event
                          ) =>
                            updateRecord(
                              item.id,
                              "title",
                              event.target
                                .value
                            )
                          }
                          sx={{
                            minWidth: 0,

                            "& .MuiOutlinedInput-root":
                              {
                                borderRadius:
                                  2.5,
                              },
                          }}
                        />

                        <TextField
                          size="small"
                          type="number"
                          label={
                            TT.stockQty
                          }
                          value={
                            item.stok_adedi ??
                            0
                          }
                          onChange={(
                            event
                          ) =>
                            updateRecord(
                              item.id,
                              "stok_adedi",
                              event.target
                                .value
                            )
                          }
                          inputProps={{
                            min: 0,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root":
                              {
                                borderRadius:
                                  2.5,
                              },
                          }}
                        />

                        <TextField
                          size="small"
                          type="number"
                          label={
                            TT.minStock
                          }
                          value={
                            item.minimum_stok ??
                            0
                          }
                          onChange={(
                            event
                          ) =>
                            updateRecord(
                              item.id,
                              "minimum_stok",
                              event.target
                                .value
                            )
                          }
                          inputProps={{
                            min: 0,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root":
                              {
                                borderRadius:
                                  2.5,
                              },
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs: "1fr 1fr",

                              md: "auto auto",
                            },

                          gap: 0.8,
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={() =>
                            onSave(item)
                          }
                          sx={{
                            minHeight:
                              44,

                            borderRadius:
                              2.5,

                            textTransform:
                              "none",

                            fontWeight:
                              950,
                          }}
                        >
                          {TT.save}
                        </Button>

                        <Button
                          color="error"
                          variant="outlined"
                          onClick={() =>
                            onDelete(
                              item
                            )
                          }
                          sx={{
                            minHeight:
                              44,

                            borderRadius:
                              2.5,

                            textTransform:
                              "none",

                            fontWeight:
                              950,

                            borderWidth:
                              2,
                          }}
                        >
                          {TT.delete}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                </Card>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ReportsPanel({
  statsPeriod,
  setStatsPeriod,
  statsData,
  productSales,
  loading,
  TT,
  lang,
}) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        mb: {
          xs: 1.5,
          md: 3,
        },

        overflow: "hidden",

        borderRadius: {
          xs: 3.5,
          md: 5,
        },

        border:
          "1px solid rgba(226,232,240,.9)",

        boxShadow:
          "0 18px 46px rgba(15,23,42,.075)",

        background:
          "linear-gradient(145deg,#fff,#f8fafc)",
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.4,
            sm: 2,
            md: 2.6,
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          spacing={1.3}
          sx={{
            mb: 1.8,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: BRAND.navy,

                fontWeight: 950,

                fontSize: {
                  xs: 18,
                  md: 21,
                },
              }}
            >
              📈 {TT.reports}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color:
                  "text.secondary",

                fontWeight: 700,
              }}
            >
              {TT.reportsSub}
            </Typography>
          </Box>

          <ToggleButtonGroup
            exclusive
            value={statsPeriod}
            onChange={(
              _,
              value
            ) =>
              value &&
              setStatsPeriod(value)
            }
            size="small"
            sx={{
              alignSelf: {
                xs: "stretch",
                sm: "center",
              },

              "& .MuiToggleButton-root":
                {
                  flex: {
                    xs: 1,
                    sm: "initial",
                  },

                  px: 2,
                  py: 0.9,

                  borderRadius:
                    "12px !important",

                  mx: 0.3,

                  textTransform:
                    "none",

                  fontWeight: 950,
                },
            }}
          >
            <ToggleButton value="day">
              {TT.daily}
            </ToggleButton>

            <ToggleButton value="month">
              {TT.monthly}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                lg: "minmax(0,1.4fr) minmax(300px,.8fr)",
              },

              gap: 1.5,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  mb: 1,

                  fontWeight: 950,

                  color:
                    BRAND.navy,
                }}
              >
                {TT.revenue}
              </Typography>

              <Stack
                spacing={1}
                sx={{
                  display: {
                    xs: "flex",
                    sm: "none",
                  },
                }}
              >
                {statsData.length ===
                0 ? (
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius:
                        3,
                    }}
                  >
                    {TT.noSales}
                  </Alert>
                ) : (
                  statsData.map(
                    (
                      row,
                      index
                    ) => {
                      const dateValue =
                        statsPeriod ===
                        "day"
                          ? row.date
                          : row.date_trunc;

                      return (
                        <Box
                          key={
                            index
                          }
                          sx={{
                            p: 1.2,

                            borderRadius:
                              3,

                            display:
                              "grid",

                            gridTemplateColumns:
                              "minmax(0,1fr) auto",

                            gap: 1,

                            alignItems:
                              "center",

                            bgcolor:
                              alpha(
                                theme
                                  .palette
                                  .primary
                                  .main,
                                0.035
                              ),

                            border:
                              `1px solid ${alpha(
                                theme
                                  .palette
                                  .primary
                                  .main,
                                0.1
                              )}`,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth:
                                0,
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  BRAND.navy,

                                fontWeight:
                                  950,
                              }}
                            >
                              {dateValue
                                ? new Date(
                                    dateValue
                                  ).toLocaleDateString(
                                    getLocale(
                                      lang
                                    )
                                  )
                                : "-"}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  "text.secondary",

                                fontWeight:
                                  750,
                              }}
                            >
                              {
                                row.siparis_sayisi
                              }{" "}
                              {TT.orderCount.toLowerCase()}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              color:
                                BRAND.blueDark,

                              fontWeight:
                                950,

                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {Number(
                              row.ciro ||
                                0
                            ).toFixed(
                              2
                            )}{" "}
                            TL
                          </Typography>
                        </Box>
                      );
                    }
                  )
                )}
              </Stack>

              <Box
                sx={{
                  display: {
                    xs: "none",
                    sm: "block",
                  },

                  overflowX:
                    "auto",

                  borderRadius: 3,

                  border:
                    "1px solid rgba(226,232,240,.9)",
                }}
              >
                <table
                  style={{
                    width: "100%",

                    minWidth: 520,

                    borderCollapse:
                      "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          "#f8fafc",
                      }}
                    >
                      <th
                        style={{
                          padding: 13,

                          textAlign:
                            "left",

                          color:
                            "#475569",
                        }}
                      >
                        {TT.period}
                      </th>

                      <th
                        style={{
                          padding: 13,

                          textAlign:
                            "left",

                          color:
                            "#475569",
                        }}
                      >
                        {TT.orderCount}
                      </th>

                      <th
                        style={{
                          padding: 13,

                          textAlign:
                            "right",

                          color:
                            "#475569",
                        }}
                      >
                        {TT.revenue}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {statsData.map(
                      (
                        row,
                        index
                      ) => {
                        const dateValue =
                          statsPeriod ===
                          "day"
                            ? row.date
                            : row.date_trunc;

                        return (
                          <tr
                            key={
                              index
                            }
                            style={{
                              borderBottom:
                                "1px solid #e5e7eb",
                            }}
                          >
                            <td
                              style={{
                                padding:
                                  13,

                                fontWeight:
                                  700,
                              }}
                            >
                              {dateValue
                                ? new Date(
                                    dateValue
                                  ).toLocaleDateString(
                                    getLocale(
                                      lang
                                    )
                                  )
                                : "-"}
                            </td>

                            <td
                              style={{
                                padding:
                                  13,

                                fontWeight:
                                  800,
                              }}
                            >
                              {
                                row.siparis_sayisi
                              }
                            </td>

                            <td
                              style={{
                                padding:
                                  13,

                                textAlign:
                                  "right",

                                whiteSpace:
                                  "nowrap",

                                color:
                                  BRAND.blueDark,

                                fontWeight:
                                  950,
                              }}
                            >
                              {Number(
                                row.ciro ||
                                  0
                              ).toFixed(
                                2
                              )}{" "}
                              TL
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  mb: 1,

                  fontWeight: 950,

                  color:
                    BRAND.navy,
                }}
              >
                {TT.topProducts}
              </Typography>

              {productSales.length ===
              0 ? (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                  }}
                >
                  {TT.noSales}
                </Alert>
              ) : (
                <Stack spacing={0.9}>
                  {productSales
                    .slice(0, 10)
                    .map(
                      (
                        product,
                        index
                      ) => (
                        <Box
                          key={`${product.title || product.urun_adi}-${index}`}
                          sx={{
                            display:
                              "grid",

                            gridTemplateColumns:
                              "auto minmax(0,1fr) auto",

                            alignItems:
                              "center",

                            gap: 1,

                            p: 1,

                            borderRadius:
                              2.7,

                            bgcolor:
                              alpha(
                                theme
                                  .palette
                                  .warning
                                  .main,
                                0.045
                              ),

                            border:
                              `1px solid ${alpha(
                                theme
                                  .palette
                                  .warning
                                  .main,
                                0.12
                              )}`,
                          }}
                        >
                          <Avatar
                            sx={{
                              width:
                                34,

                              height:
                                34,

                              bgcolor:
                                alpha(
                                  theme
                                    .palette
                                    .warning
                                    .main,
                                  0.13
                                ),

                              color:
                                "warning.dark",

                              fontSize:
                                13,

                              fontWeight:
                                950,
                            }}
                          >
                            {index +
                              1}
                          </Avatar>

                          <Typography
                            sx={{
                              minWidth:
                                0,

                              overflow:
                                "hidden",

                              textOverflow:
                                "ellipsis",

                              whiteSpace:
                                "nowrap",

                              color:
                                BRAND.navy,

                              fontWeight:
                                850,
                            }}
                          >
                            {product.title ||
                              product.urun_adi ||
                              product.name ||
                              "-"}
                          </Typography>

                          <Chip
                            size="small"
                            label={`${
                              product.toplam_adet ||
                              product.adet ||
                              product.quantity ||
                              0
                            } ${
                              TT.piece
                            }`}
                            sx={{
                              fontWeight:
                                900,
                            }}
                          />
                        </Box>
                      )
                    )}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function WaiterCallsDialog({
  open,
  onClose,
  calls,
  unreadCount,
  onMarkRead,
  TT,
  lang,
}) {
  const theme = useTheme();

  const mobile =
    useMediaQuery(
      theme.breakpoints.down("sm")
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={mobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          overflow: "hidden",

          borderRadius: {
            xs: 0,
            sm: 4.5,
          },

          background:
            "linear-gradient(145deg,#fff,#fff7ed)",

          boxShadow:
            "0 34px 90px rgba(15,23,42,.28)",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,

          color: "#fff",

          background:
            "linear-gradient(135deg,#0f172a 0%,#1e3a8a 55%,#f97316 100%)",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 2.5,
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.4}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
              sx={{
                minWidth: 0,
              }}
            >
              <Badge
                badgeContent={
                  unreadCount
                }
                color="error"
                max={99}
              >
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,

                    bgcolor:
                      "rgba(255,255,255,.17)",

                    border:
                      "1px solid rgba(255,255,255,.28)",

                    animation:
                      unreadCount > 0
                        ? `${pulse} 1.5s ease-in-out infinite`
                        : "none",
                  }}
                >
                  <RoomServiceIcon />
                </Avatar>
              </Badge>

              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 950,

                    fontSize: {
                      xs: 20,
                      sm: 24,
                    },

                    lineHeight: 1.1,
                  }}
                >
                  {TT.waiterCalls}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.4,

                    color:
                      "rgba(255,255,255,.78)",
                  }}
                >
                  {TT.waiterSubtitle}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={onClose}
              sx={{
                color: "#fff",

                bgcolor:
                  "rgba(255,255,255,.12)",

                border:
                  "1px solid rgba(255,255,255,.18)",
              }}
            >
              <CancelIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: {
            xs: 1.3,
            sm: 2.2,
          },
        }}
      >
        {calls.length === 0 ? (
          <Box
            sx={{
              py: 7,
              px: 2,

              textAlign: "center",

              borderRadius: 4,

              bgcolor:
                "rgba(255,255,255,.75)",

              border:
                "1px dashed rgba(15,23,42,.16)",
            }}
          >
            <Avatar
              sx={{
                width: 70,
                height: 70,

                mx: "auto",
                mb: 1.5,

                bgcolor: alpha(
                  theme.palette.success
                    .main,
                  0.12
                ),

                color:
                  "success.main",
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 36,
                }}
              />
            </Avatar>

            <Typography
              sx={{
                color:
                  BRAND.navy,

                fontWeight: 950,
              }}
            >
              {TT.noWaiter}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,

                color:
                  "text.secondary",

                fontWeight: 700,
              }}
            >
              {TT.waiterEmptySubtitle}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.1}>
            {calls.map((call) => {
              const unread =
                !call.is_read;

              const tableLabel =
                getTableLabel(call) ||
                TT.unspecified;

              return (
                <Accordion
                  key={call.id}
                  disableGutters
                  elevation={0}
                  defaultExpanded={
                    unread
                  }
                  sx={{
                    overflow:
                      "hidden",

                    borderRadius:
                      "18px !important",

                    border:
                      `1px solid ${
                        unread
                          ? alpha(
                              theme
                                .palette
                                .warning
                                .main,
                              0.34
                            )
                          : alpha(
                              theme
                                .palette
                                .grey[500],
                              0.14
                            )
                      }`,

                    borderLeft:
                      `6px solid ${
                        unread
                          ? theme
                              .palette
                              .warning
                              .main
                          : theme
                              .palette
                              .grey[400]
                      }`,

                    bgcolor:
                      unread
                        ? alpha(
                            theme
                              .palette
                              .warning
                              .main,
                            0.055
                          )
                        : "rgba(255,255,255,.82)",

                    animation:
                      `${fadeInUp} .3s ease both`,

                    "&:before": {
                      display:
                        "none",
                    },

                    "&.Mui-expanded":
                      {
                        m: 0,
                      },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon />
                    }
                    sx={{
                      minHeight: 78,

                      px: {
                        xs: 1.2,
                        sm: 1.7,
                      },

                      "& .MuiAccordionSummary-content":
                        {
                          minWidth:
                            0,

                          my: 1,
                        },
                    }}
                  >
                    <Box
                      sx={{
                        width:
                          "100%",

                        minWidth: 0,

                        display:
                          "grid",

                        gridTemplateColumns:
                          "auto minmax(0,1fr) auto",

                        gap: 1.1,

                        alignItems:
                          "center",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,

                          bgcolor:
                            unread
                              ? alpha(
                                  theme
                                    .palette
                                    .warning
                                    .main,
                                  0.16
                                )
                              : alpha(
                                  theme
                                    .palette
                                    .grey[500],
                                  0.1
                                ),

                          color:
                            unread
                              ? "warning.dark"
                              : "text.secondary",
                        }}
                      >
                        <TableRestaurantIcon />
                      </Avatar>

                      <Box
                        sx={{
                          minWidth: 0,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.7}
                          alignItems="center"
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Typography
                            sx={{
                              color:
                                BRAND.navy,

                              fontWeight:
                                950,
                            }}
                          >
                            {TT.table}{" "}
                            {tableLabel}
                          </Typography>

                          {unread && (
                            <Chip
                              size="small"
                              color="warning"
                              label={
                                TT.newCall
                              }
                              sx={{
                                height:
                                  24,

                                fontWeight:
                                  900,
                              }}
                            />
                          )}
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.3,

                            color:
                              "text.secondary",

                            fontWeight:
                              750,

                            overflow:
                              "hidden",

                            textOverflow:
                              "ellipsis",

                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {call.musteri_adi ||
                            TT.guest}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          display: {
                            xs: "none",
                            sm: "block",
                          },

                          color:
                            "text.secondary",

                          fontWeight:
                            750,

                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {formatDateValue(
                          call.created_at,
                          lang
                        )}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      px: {
                        xs: 1.2,
                        sm: 1.7,
                      },

                      pt: 0,
                      pb: 1.5,
                    }}
                  >
                    <Divider
                      sx={{
                        mb: 1.2,

                        borderStyle:
                          "dashed",
                      }}
                    />

                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      justifyContent="space-between"
                      alignItems={{
                        xs: "stretch",
                        sm: "center",
                      }}
                      spacing={1}
                    >
                      <Stack
                        direction="row"
                        spacing={0.7}
                        alignItems="center"
                      >
                        <AccessTimeIcon
                          sx={{
                            fontSize:
                              18,

                            color:
                              "text.secondary",
                          }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              "text.secondary",

                            fontWeight:
                              750,
                          }}
                        >
                          {formatDateValue(
                            call.created_at,
                            lang
                          )}
                        </Typography>
                      </Stack>

                      {unread ? (
                        <Button
                          variant="contained"
                          startIcon={
                            <CheckCircleIcon />
                          }
                          onClick={() =>
                            onMarkRead(
                              call.id
                            )
                          }
                          sx={{
                            minHeight:
                              46,

                            borderRadius:
                              2.7,

                            textTransform:
                              "none",

                            fontWeight:
                              950,
                          }}
                        >
                          {TT.markRead}
                        </Button>
                      ) : (
                        <Chip
                          color="success"
                          icon={
                            <CheckCircleIcon />
                          }
                          label={
                            TT.seen
                          }
                          sx={{
                            fontWeight:
                              900,
                          }}
                        />
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: {
            xs: 1.3,
            sm: 2,
          },

          borderTop:
            "1px solid rgba(226,232,240,.9)",

          bgcolor:
            "rgba(255,255,255,.85)",
        }}
      >
        <Button
          fullWidth
          onClick={onClose}
          sx={{
            minHeight: 46,

            borderRadius: 2.7,

            textTransform: "none",

            fontWeight: 950,
          }}
        >
          {TT.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function KitchenPrintDialog({
  open,
  order,
  TT,
  lang,
  paperSize,
  onPaperSizeChange,
  includePrices,
  onIncludePricesChange,
  onClose,
  onPrintResult,
}) {
  if (!order) return null;

  const tableLabel =
    getTableLabel(order) || TT.unspecified;

  const itemCount = (order.items || []).reduce(
    (sum, item) => sum + getItemQty(item),
    0
  );

  const handlePrint = () => {
    const opened = openKitchenPrintWindow({
      order,
      paperSize,
      includePrices,
      lang,
      TT,
    });

    onPrintResult(opened);

    if (opened) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#fff",
          bgcolor: BRAND.navy,
          borderBottom: `5px solid ${BRAND.orange}`,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar sx={{ bgcolor: BRAND.orange, color: "#fff" }}>
            <PrintIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 950, lineHeight: 1.15 }}>
              {TT.kitchenPrintTitle} · #{order.id}
            </Typography>
            <Typography sx={{ mt: 0.35, color: "rgba(255,255,255,.75)", fontSize: 12.5, fontWeight: 700 }}>
              {TT.kitchenPrintSubtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.2 } }}>
        <Box
          sx={{
            mt: 0.5,
            mb: 2,
            p: 1.4,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: 0.8,
            border: "1px solid #dbe4ee",
            borderRadius: 3,
            bgcolor: "#f8fafc",
          }}
        >
          <Box>
            <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 850 }}>
              {TT.orderNo}
            </Typography>
            <Typography sx={{ color: BRAND.navy, fontSize: 18, fontWeight: 950 }}>
              #{order.id}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 850 }}>
              {TT.table}
            </Typography>
            <Typography sx={{ color: BRAND.navy, fontSize: 18, fontWeight: 950 }}>
              {tableLabel}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 850 }}>
              {TT.products}
            </Typography>
            <Typography sx={{ color: BRAND.navy, fontSize: 18, fontWeight: 950 }}>
              {itemCount} {TT.piece}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ mb: 0.8, color: BRAND.navy, fontSize: 13, fontWeight: 950 }}>
          {TT.paperSize}
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          value={paperSize}
          onChange={(_, value) => {
            if (value) onPaperSizeChange(value);
          }}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: 0.8,
            "& .MuiToggleButton-root": {
              m: "0 !important",
              minHeight: 48,
              border: "1px solid #cbd5e1 !important",
              borderRadius: "10px !important",
              textTransform: "none",
              fontWeight: 900,
            },
          }}
        >
          <ToggleButton value="58">{TT.paper58}</ToggleButton>
          <ToggleButton value="80">{TT.paper80}</ToggleButton>
          <ToggleButton value="a4">{TT.paperA4}</ToggleButton>
        </ToggleButtonGroup>

        <Button
          type="button"
          fullWidth
          variant={includePrices ? "contained" : "outlined"}
          color={includePrices ? "primary" : "inherit"}
          onClick={() => onIncludePricesChange(!includePrices)}
          sx={{
            mt: 1.2,
            minHeight: 46,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 900,
          }}
        >
          {includePrices ? "✓ " : ""}
          {TT.includePrices}
        </Button>

        <Alert severity="info" sx={{ mt: 1.4, borderRadius: 3, fontWeight: 700 }}>
          {TT.printHelp}
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ minHeight: 44, borderRadius: 2.5, textTransform: "none", fontWeight: 850 }}
        >
          {TT.close}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{
            minHeight: 46,
            px: 2.5,
            borderRadius: 2.5,
            bgcolor: BRAND.orange,
            textTransform: "none",
            fontWeight: 950,
            "&:hover": { bgcolor: BRAND.orangeDark },
          }}
        >
          {TT.printNow}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SiparislerPage() {
  const theme = useTheme();

  const token =
    localStorage.getItem("token");

  const [lang, setLang] =
    useState(getPanelLang);

  const TT =
    TEXT[lang] || TEXT.tr;

  const [
    soundEnabled,
    setSoundEnabled,
  ] = useState(() => {
    return (
      localStorage.getItem(
        "soundEnabled"
      ) === "true"
    );
  });

  const [
    filterStatus,
    setFilterStatus,
  ] = useState("all");

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  const [page, setPage] =
    useState(1);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      message: "",
      severity: "info",
    });

  const [
    centerAlert,
    setCenterAlert,
  ] = useState({
    open: false,
    type: "order",
    icon: "🔔",
    title: "",
    message: "",
    subtitle: "",
  });

  const [
    cancelDialogOpen,
    setCancelDialogOpen,
  ] = useState(false);

  const [
    selectedOrderId,
    setSelectedOrderId,
  ] = useState(null);

  const [
    kitchenPrintOrder,
    setKitchenPrintOrder,
  ] = useState(null);

  const [
    kitchenPaperSize,
    setKitchenPaperSize,
  ] = useState(() => {
    const saved = localStorage.getItem(
      "kitchen_printer_paper_size"
    );

    return ["58", "80", "a4"].includes(saved)
      ? saved
      : "80";
  });

  const [
    kitchenIncludePrices,
    setKitchenIncludePrices,
  ] = useState(() => {
    return (
      localStorage.getItem(
        "kitchen_printer_include_prices"
      ) === "true"
    );
  });

  const [
    statsPeriod,
    setStatsPeriod,
  ] = useState("day");

  const [
    statsData,
    setStatsData,
  ] = useState([]);

  const [
    productSales,
    setProductSales,
  ] = useState([]);

  const [
    statsLoading,
    setStatsLoading,
  ] = useState(false);

  const [stocks, setStocks] =
    useState([]);

  const [
    stockLoading,
    setStockLoading,
  ] = useState(false);

  const [
    waiterCalls,
    setWaiterCalls,
  ] = useState([]);

  const [
    unreadWaiterCount,
    setUnreadWaiterCount,
  ] = useState(0);

  const [
    showWaiterDialog,
    setShowWaiterDialog,
  ] = useState(false);

  const centerTimerRef =
    useRef(null);

  const previousOrdersRef =
    useRef([]);

  const previousWaiterCallsRef =
    useRef([]);

  const audioRef = useRef(null);

  const waiterAudioRef =
    useRef(null);

  const {
    orders,
    loading,
    error,
    updateOrderStatus,
    refetch,
  } = useOrders(
    token,
    ORDER_POLL_INTERVAL_MS
  );

  const showCenterAlert =
    useCallback((payload) => {
      if (
        centerTimerRef.current
      ) {
        clearTimeout(
          centerTimerRef.current
        );
      }

      setCenterAlert({
        open: true,

        type:
          payload.type ||
          "order",

        icon:
          payload.icon ||
          "🔔",

        title:
          payload.title || "",

        message:
          payload.message ||
          "",

        subtitle:
          payload.subtitle ||
          "",
      });

      centerTimerRef.current =
        setTimeout(() => {
          setCenterAlert(
            (current) => ({
              ...current,

              open: false,
            })
          );
        }, 4200);
    }, []);

  const playSound =
    useCallback(
      (audioElement) => {
        if (
          !soundEnabled ||
          !audioElement
        ) {
          return;
        }

        try {
          audioElement.currentTime =
            0;

          const promise =
            audioElement.play();

          if (
            promise?.catch
          ) {
            promise.catch(
              () => {}
            );
          }
        } catch {
          /*
           * Tarayıcı sesi engellerse
           * sayfa çalışmaya devam eder.
           */
        }
      },
      [soundEnabled]
    );

  useEffect(() => {
    return () => {
      if (
        centerTimerRef.current
      ) {
        clearTimeout(
          centerTimerRef.current
        );
      }
    };
  }, []);

  useEffect(() => {
    const syncLanguage =
      () =>
        setLang(
          getPanelLang()
        );

    window.addEventListener(
      "app_lang_change",
      syncLanguage
    );

    window.addEventListener(
      "storage",
      syncLanguage
    );

    return () => {
      window.removeEventListener(
        "app_lang_change",
        syncLanguage
      );

      window.removeEventListener(
        "storage",
        syncLanguage
      );
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "soundEnabled",
      String(soundEnabled)
    );
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem(
      "kitchen_printer_paper_size",
      kitchenPaperSize
    );
  }, [kitchenPaperSize]);

  useEffect(() => {
    localStorage.setItem(
      "kitchen_printer_include_prices",
      String(kitchenIncludePrices)
    );
  }, [kitchenIncludePrices]);

  const fetchStocks =
    useCallback(async () => {
      if (!token) return;

      setStockLoading(true);

      try {
        const response =
          await fetch(
            `${API_BASE}/menu/stock`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (json.ok) {
          setStocks(
            json.data || []
          );
        }
      } catch (stockError) {
        console.error(
          "Stok alınamadı:",
          stockError
        );
      } finally {
        setStockLoading(false);
      }
    }, [token]);

  const saveStock =
    async (item) => {
      const productName =
        String(
          item.title ||
            item.urun_adi ||
            ""
        ).trim();

      if (!productName) {
        setSnackbar({
          open: true,

          message:
            TT.stockNameRequired,

          severity: "error",
        });

        return;
      }

      try {
        const isNew =
          String(
            item.id
          ).startsWith(
            "new-"
          );

        const response =
          await fetch(
            isNew
              ? `${API_BASE}/menu/stock`
              : `${API_BASE}/menu/stock/${item.id}`,
            {
              method: isNew
                ? "POST"
                : "PUT",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  urun_adi:
                    productName,

                  stok_adedi:
                    Number(
                      item.stok_adedi ||
                        0
                    ),

                  minimum_stok:
                    Number(
                      item.minimum_stok ||
                        0
                    ),

                  stok_takibi:
                    true,
                }),
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          !json.ok
        ) {
          throw new Error(
            json.message ||
              TT.stockSaveFail
          );
        }

        await fetchStocks();

        setSnackbar({
          open: true,

          message:
            TT.stockSaved.replace(
              "{name}",
              productName
            ),

          severity: "success",
        });
      } catch (saveError) {
        setSnackbar({
          open: true,

          message:
            saveError.message ||
            TT.stockSaveFail,

          severity: "error",
        });
      }
    };

  const deleteStock =
    async (item) => {
      if (
        String(
          item.id
        ).startsWith(
          "new-"
        )
      ) {
        setStocks(
          (current) =>
            current.filter(
              (row) =>
                String(
                  row.id
                ) !==
                String(
                  item.id
                )
            )
        );

        return;
      }

      try {
        const response =
          await fetch(
            `${API_BASE}/menu/stock/${item.id}`,
            {
              method: "DELETE",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          !json.ok
        ) {
          throw new Error(
            json.message ||
              TT.stockDeleteFail
          );
        }

        await fetchStocks();

        setSnackbar({
          open: true,

          message:
            TT.stockDeleted,

          severity: "success",
        });
      } catch (
        deleteError
      ) {
        setSnackbar({
          open: true,

          message:
            deleteError.message ||
            TT.stockDeleteFail,

          severity: "error",
        });
      }
    };

  const fetchStatistics =
    useCallback(async () => {
      if (!token) return;

      setStatsLoading(true);

      try {
        const [
          statsResponse,
          productsResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE}/menu/orders-statistics?groupBy=${statsPeriod}`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,

                "Content-Type":
                  "application/json",
              },
            }
          ),

          fetch(
            `${API_BASE}/menu/product-sales?limit=10`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,

                "Content-Type":
                  "application/json",
              },
            }
          ),
        ]);

        const statsJson =
          await statsResponse
            .json()
            .catch(() => ({}));

        const productsJson =
          await productsResponse
            .json()
            .catch(() => ({}));

        if (statsJson.ok) {
          setStatsData(
            statsJson.data || []
          );
        }

        if (productsJson.ok) {
          setProductSales(
            productsJson.data ||
              []
          );
        }
      } catch (
        statisticsError
      ) {
        console.error(
          "Raporlama hatası:",
          statisticsError
        );
      } finally {
        setStatsLoading(false);
      }
    }, [statsPeriod, token]);

  const fetchWaiterCalls =
    useCallback(async () => {
      if (!token) return;

      try {
        const response =
          await fetch(
            `${API_BASE}/waiter-calls`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (!json.ok) return;

        const rows =
          json.data || [];

        setWaiterCalls(rows);

        setUnreadWaiterCount(
          rows.filter(
            (call) =>
              !call.is_read
          ).length
        );

        if (
          previousWaiterCallsRef
            .current.length > 0
        ) {
          const oldIds =
            new Set(
              previousWaiterCallsRef.current.map(
                (call) =>
                  call.id
              )
            );

          const newCalls =
            rows.filter(
              (call) =>
                !oldIds.has(
                  call.id
                ) &&
                !call.is_read
            );

          if (
            newCalls.length > 0
          ) {
            playSound(
              waiterAudioRef.current
            );

            const tableLabel =
              getTableLabel(
                newCalls[0]
              );

            showCenterAlert({
              type: "waiter",

              icon: "🔔",

              title:
                TT.waiterCalls,

              message:
                TT.newWaiterMsg.replace(
                  "{count}",
                  newCalls.length
                ),

              subtitle:
                tableLabel
                  ? `${TT.table} ${tableLabel}`
                  : TT.unspecified,
            });

            setSnackbar({
              open: true,

              message:
                TT.newWaiterMsg.replace(
                  "{count}",
                  newCalls.length
                ),

              severity: "info",
            });
          }
        }

        previousWaiterCallsRef.current =
          rows;
      } catch (waiterError) {
        console.error(
          "Garson çağrıları alınamadı:",
          waiterError
        );
      }
    }, [
      TT,
      playSound,
      showCenterAlert,
      token,
    ]);

  const markWaiterCallAsRead =
    async (callId) => {
      try {
        await fetch(
          `${API_BASE}/waiter-calls/${callId}/read`,
          {
            method: "PUT",

            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        await fetchWaiterCalls();
      } catch (readError) {
        console.error(
          "Okundu işaretlenemedi:",
          readError
        );
      }
    };

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    fetchWaiterCalls();

    const timer = setInterval(
      fetchWaiterCalls,
      WAITER_POLL_INTERVAL_MS
    );

    return () =>
      clearInterval(timer);
  }, [
    fetchWaiterCalls,
    token,
  ]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const instantRefresh =
      () => {
        if (
          document.visibilityState ===
          "visible"
        ) {
          refetch();

          fetchWaiterCalls();
        }
      };

    window.addEventListener(
      "focus",
      instantRefresh
    );

    document.addEventListener(
      "visibilitychange",
      instantRefresh
    );

    return () => {
      window.removeEventListener(
        "focus",
        instantRefresh
      );

      document.removeEventListener(
        "visibilitychange",
        instantRefresh
      );
    };
  }, [
    fetchWaiterCalls,
    refetch,
    token,
  ]);

  useEffect(() => {
    if (
      previousOrdersRef.current
        .length > 0
    ) {
      const oldIds = new Set(
        previousOrdersRef.current.map(
          (order) => order.id
        )
      );

      const newOrders =
        orders.filter(
          (order) =>
            !oldIds.has(
              order.id
            )
        );

      if (
        newOrders.length > 0
      ) {
        playSound(
          audioRef.current
        );

        const tableLabel =
          getTableLabel(
            newOrders[0]
          );

        showCenterAlert({
          type: "order",

          icon: "🛒",

          title: TT.pageTitle,

          message:
            TT.newOrderMsg.replace(
              "{count}",
              newOrders.length
            ),

          subtitle:
            tableLabel
              ? `${TT.table} ${tableLabel}`
              : `#${
                  newOrders[0]
                    ?.id || ""
                }`,
        });

        setSnackbar({
          open: true,

          message:
            TT.newOrderMsg.replace(
              "{count}",
              newOrders.length
            ),

          severity: "info",
        });
      }
    }

    previousOrdersRef.current =
      orders;
  }, [
    TT,
    orders,
    playSound,
    showCenterAlert,
  ]);

  const transferOrderToCash =
    useCallback(
      async (order) => {
        if (!order) {
          return {
            skipped: true,
            message: "",
          };
        }

        const amount =
          getOrderTotal(order);

        const currency =
          getOrderCurrency(order);

        if (amount <= 0) {
          return {
            skipped: true,

            message:
              TT.cashZero,
          };
        }

        const marker =
          `QR_ORDER_ID:${order.id}`;

        const listResponse =
          await fetch(
            `${API_BASE}/cash`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        if (listResponse.ok) {
          const listJson =
            await listResponse
              .json()
              .catch(() => ({}));

          const exists = (
            listJson.data || []
          ).some(
            (record) =>
              record.module_type ===
                "menu" &&
              String(
                record.note ||
                  ""
              ).includes(
                marker
              )
          );

          if (exists) {
            return {
              skipped: true,

              message:
                TT.cashAlready,
            };
          }
        }

        const itemSummary = (
          order.items || []
        )
          .map(
            (item) =>
              `${getItemQty(
                item
              )}x ${getItemTitle(
                item
              )}`
          )
          .join(", ");

        const tableLabel =
          getTableLabel(order);

        const body = {
          module_type: "menu",

          transaction_type:
            "income",

          title:
            `QR Menü Sipariş #${order.id}`,

          category:
            "QR Menü Sipariş",

          quantity: 1,

          unit_price: amount,

          total_amount: amount,

          currency,

          payment_method:
            normalizePaymentMethod(
              order.payment_method ||
                order.odeme_tipi ||
                order.odeme_turu ||
                order.paymentType ||
                "cash"
            ),

          customer_id: null,

          customer_name:
            order.musteri_adi ||
            "",

          staff_name: "",

          note: [
            marker,

            tableLabel
              ? `Masa: ${tableLabel}`
              : "",

            order.telefon
              ? `Telefon: ${order.telefon}`
              : "",

            itemSummary
              ? `Ürünler: ${itemSummary}`
              : "",

            order.siparis_notu
              ? `Sipariş Notu: ${order.siparis_notu}`
              : "",
          ]
            .filter(Boolean)
            .join(" | "),

          transaction_date:
            String(
              order.created_at ||
                new Date().toISOString()
            ).slice(0, 10),
        };

        const response =
          await fetch(
            `${API_BASE}/cash`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify(
                  body
                ),
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          json.ok === false
        ) {
          throw new Error(
            json.message ||
              TT.cashFail
          );
        }

        return {
          skipped: false,

          message:
            TT.statusAndCash.replace(
              "{id}",
              order.id
            ),
        };
      },
      [TT, token]
    );

  const handleStatusUpdate =
    async (
      id,
      newStatus
    ) => {
      const selectedOrder =
        orders.find(
          (order) =>
            String(order.id) ===
            String(id)
        );

      try {
        await updateOrderStatus(
          id,
          newStatus
        );

        if (
          newStatus ===
          "tamamlandi"
        ) {
          try {
            const cashResult =
              await transferOrderToCash(
                selectedOrder
              );

            setSnackbar({
              open: true,

              message:
                cashResult?.message ||
                TT.statusAndCash.replace(
                  "{id}",
                  id
                ),

              severity:
                cashResult?.skipped
                  ? "info"
                  : "success",
            });

            return true;
          } catch (cashError) {
            setSnackbar({
              open: true,

              message:
                cashError.message ||
                TT.cashFail,

              severity:
                "warning",
            });

            return true;
          }
        }

        setSnackbar({
          open: true,

          message:
            TT.statusUpdated.replace(
              "{id}",
              id
            ),

          severity: "success",
        });

        return true;
      } catch (
        statusError
      ) {
        setSnackbar({
          open: true,

          message:
            statusError.message ||
            "Güncelleme başarısız",

          severity: "error",
        });

        return false;
      }
    };

  const deleteOrderPermanently =
    async (id) => {
      if (
        !window.confirm(
          TT.deleteConfirm.replace(
            "{id}",
            id
          )
        )
      ) {
        return;
      }

      try {
        const response =
          await fetch(
            `${API_BASE}/qr-orders/${id}`,
            {
              method: "DELETE",

              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await response
            .json()
            .catch(() => ({}));

        if (
          !response.ok ||
          json.ok === false
        ) {
          throw new Error(
            json.message ||
              TT.deleteFail
          );
        }

        await refetch();

        setSnackbar({
          open: true,

          message:
            TT.deleteSuccess.replace(
              "{id}",
              id
            ),

          severity: "success",
        });
      } catch (
        deleteError
      ) {
        setSnackbar({
          open: true,

          message:
            deleteError.message ||
            TT.deleteFail,

          severity: "error",
        });
      }
    };

  const filteredOrders =
    useMemo(() => {
      let result = [
        ...orders,
      ];

      if (
        filterStatus !== "all"
      ) {
        result = result.filter(
          (order) =>
            order.durum ===
            filterStatus
        );
      }

      const term =
        searchTerm
          .trim()
          .toLowerCase();

      if (term) {
        result =
          result.filter(
            (order) =>
              [
                order.id,

                getTableLabel(
                  order
                ),

                order.musteri_adi,

                order.telefon,

                order.siparis_notu,
              ]
                .filter(Boolean)
                .some((value) =>
                  String(value)
                    .toLowerCase()
                    .includes(term)
                )
          );
      }

      result.sort(
        (a, b) =>
          Number(b.id) -
          Number(a.id)
      );

      return result;
    }, [
      filterStatus,
      orders,
      searchTerm,
    ]);

  useEffect(() => {
    setPage(1);
  }, [
    filterStatus,
    searchTerm,
  ]);

  const pageCount =
    Math.max(
      1,

      Math.ceil(
        filteredOrders.length /
          PAGE_SIZE
      )
    );

  const paginatedOrders =
    filteredOrders.slice(
      (page - 1) *
        PAGE_SIZE,

      page * PAGE_SIZE
    );

  const summary = useMemo(
    () => {
      const todayKey =
        new Date()
          .toISOString()
          .slice(0, 10);

      const dailyRevenueFromOrders =
        orders.reduce(
          (sum, order) => {
            const orderDate =
              String(
                order.created_at ||
                  ""
              ).slice(0, 10);

            if (
              orderDate !==
                todayKey ||
              order.durum ===
                "iptal"
            ) {
              return sum;
            }

            return (
              sum +
              getOrderTotal(
                order
              )
            );
          },
          0
        );

      return {
        total: orders.length,

        newCount:
          orders.filter(
            (order) =>
              order.durum ===
              "yeni"
          ).length,

        criticalStock:
          stocks.filter(
            (stock) =>
              Number(
                stock.stok_adedi ||
                  0
              ) <=
              Number(
                stock.minimum_stok ||
                  0
              )
          ).length,

        dailyRevenue:
          dailyRevenueFromOrders,
      };
    },
    [orders, stocks]
  );

  const handleRefresh =
    async () => {
      setRefreshing(true);

      await Promise.all([
        refetch(),

        fetchWaiterCalls(),

        fetchStocks(),
      ]);

      setTimeout(
        () =>
          setRefreshing(
            false
          ),
        450
      );
    };

  if (
    loading &&
    orders.length === 0
  ) {
    return (
      <Box
        sx={{
          minHeight: "100vh",

          p: {
            xs: 1.5,
            md: 3,
          },

          background:
            "linear-gradient(145deg,#f8fafc,#eef2f7)",
        }}
      >
        <Box
          sx={{
            maxWidth: 1500,

            mx: "auto",
          }}
        >
          <Skeleton
            variant="rounded"
            height={160}
            sx={{
              borderRadius: 4,

              mb: 2,
            }}
          />

          {[1, 2, 3].map(
            (item) => (
              <Skeleton
                key={item}
                variant="rounded"
                height={120}
                sx={{
                  borderRadius:
                    4,

                  mb: 1.5,
                }}
              />
            )
          )}
        </Box>
      </Box>
    );
  }

  const statCards = [
    {
      label:
        TT.totalOrders,

      value:
        summary.total,

      icon: (
        <ReceiptLongIcon />
      ),

      color:
        BRAND.blue,
    },

    {
      label:
        TT.newOrders,

      value:
        summary.newCount,

      icon: (
        <NewReleasesIcon />
      ),

      color:
        BRAND.orange,
    },

    {
      label:
        TT.dailyRevenue,

      value:
        moneyText(
          summary.dailyRevenue,
          "TRY",
          lang
        ),

      icon: (
        <PaymentsOutlinedIcon />
      ),

      color:
        BRAND.green,
    },

    {
      label:
        TT.criticalStock,

      value:
        summary.criticalStock,

      icon: (
        <WarningAmberIcon />
      ),

      color:
        BRAND.red,
    },
  ];

  return (
    <>
      <audio
        ref={audioRef}
        src="/sounds/new-order.mp3"
        preload="auto"
      />

      <audio
        ref={waiterAudioRef}
        src="/sounds/waiter-call.mp3"
        preload="auto"
      />

      <CenterAlert
        data={centerAlert}
        TT={TT}
        onClose={() =>
          setCenterAlert(
            (current) => ({
              ...current,

              open: false,
            })
          )
        }
      />

      <Box
        sx={{
          minHeight: "100vh",

          width: "100%",

          maxWidth:
            "100vw",

          minWidth: 0,

          overflowX:
            "hidden",

          p: {
            xs: 1,
            sm: 1.5,
            md: 2.5,
          },

          pb: {
            xs: 5,
            md: 7,
          },

          boxSizing:
            "border-box",

          background:
            "radial-gradient(circle at top left,rgba(37,99,235,.1),transparent 32%),radial-gradient(circle at top right,rgba(249,115,22,.12),transparent 30%),linear-gradient(145deg,#f8fafc,#eef2f7)",

          "& *": {
            boxSizing:
              "border-box",
          },

          "& .MuiPaper-root":
            {
              maxWidth:
                "100%",

              minWidth: 0,
            },
        }}
      >
        <Box
          sx={{
            width: "100%",

            maxWidth: 1500,

            mx: "auto",

            minWidth: 0,
          }}
        >
          <Card
            sx={{
              mb: {
                xs: 1.5,
                md: 2.5,
              },

              position: {
                xs: "sticky",
                md: "relative",
              },

              top: 0,

              zIndex: 20,

              overflow:
                "hidden",

              borderRadius: {
                xs: 3.5,
                md: 5,
              },

              border:
                "1px solid rgba(15,23,42,.1)",

              background:
                "rgba(255,255,255,.94)",

              backdropFilter:
                "blur(16px)",

              boxShadow:
                "0 20px 60px rgba(15,23,42,.1)",

              "&:before": {
                content:
                  '""',

                display:
                  "block",

                height: 5,

                background:
                  "linear-gradient(90deg,#0b4d8b,#2563eb,#f97316)",
              },
            }}
          >
            <CardContent
              sx={{
                p: {
                  xs: 1.5,
                  sm: 2.1,
                  md: 3,
                },
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  lg: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "stretch",
                  lg: "center",
                }}
                spacing={2}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                >
                  <Avatar
                    sx={{
                      width: {
                        xs: 50,
                        md: 62,
                      },

                      height: {
                        xs: 50,
                        md: 62,
                      },

                      background:
                        "linear-gradient(135deg,#0b4d8b,#2563eb)",

                      boxShadow:
                        "0 14px 34px rgba(37,99,235,.28)",
                    }}
                  >
                    <ReceiptLongIcon
                      sx={{
                        fontSize: {
                          xs: 27,
                          md: 34,
                        },
                      }}
                    />
                  </Avatar>

                  <Box
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        color:
                          BRAND.navy,

                        fontWeight:
                          950,

                        fontSize: {
                          xs: 22,
                          sm: 27,
                          md: 34,
                        },

                        lineHeight:
                          1.08,

                        letterSpacing:
                          -0.8,
                      }}
                    >
                      {TT.pageTitle}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,

                        color:
                          "text.secondary",

                        fontWeight:
                          700,

                        maxWidth:
                          760,
                      }}
                    >
                      {TT.pageSubtitle}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns:
                      {
                        xs: "1fr",

                        sm: "repeat(3,minmax(0,1fr))",
                      },

                    gap: 1,

                    minWidth: {
                      lg: 560,
                    },
                  }}
                >
                  <Button
                    fullWidth
                    variant={
                      soundEnabled
                        ? "contained"
                        : "outlined"
                    }
                    color={
                      soundEnabled
                        ? "success"
                        : "inherit"
                    }
                    startIcon={
                      soundEnabled ? (
                        <VolumeUpIcon />
                      ) : (
                        <VolumeOffIcon />
                      )
                    }
                    onClick={() =>
                      setSoundEnabled(
                        (current) =>
                          !current
                      )
                    }
                    sx={{
                      minHeight:
                        52,

                      borderRadius:
                        3,

                      textTransform:
                        "none",

                      fontWeight:
                        950,
                    }}
                  >
                    {soundEnabled
                      ? TT.soundOn
                      : TT.soundOff}
                  </Button>

                  <Button
                    fullWidth
                    color="warning"
                    variant="contained"
                    startIcon={
                      <RoomServiceIcon />
                    }
                    onClick={() =>
                      setShowWaiterDialog(
                        true
                      )
                    }
                    sx={{
                      minHeight:
                        52,

                      borderRadius:
                        3,

                      textTransform:
                        "none",

                      fontWeight:
                        950,
                    }}
                  >
                    {TT.waiterCalls}

                    {unreadWaiterCount >
                      0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,

                          minWidth:
                            23,

                          height: 23,

                          px: 0.7,

                          borderRadius:
                            999,

                          display:
                            "inline-flex",

                          alignItems:
                            "center",

                          justifyContent:
                            "center",

                          bgcolor:
                            "error.main",

                          color:
                            "#fff",

                          fontSize:
                            12,

                          fontWeight:
                            950,
                        }}
                      >
                        {
                          unreadWaiterCount
                        }
                      </Box>
                    )}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      <RefreshIcon
                        sx={{
                          animation:
                            refreshing
                              ? `${rotate} .7s linear infinite`
                              : "none",
                        }}
                      />
                    }
                    disabled={
                      refreshing
                    }
                    onClick={
                      handleRefresh
                    }
                    sx={{
                      minHeight:
                        52,

                      borderRadius:
                        3,

                      textTransform:
                        "none",

                      fontWeight:
                        950,

                      borderWidth:
                        2,
                    }}
                  >
                    {TT.refresh}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",

                lg: "repeat(4,minmax(0,1fr))",
              },

              gap: {
                xs: 1,
                md: 1.5,
              },

              mb: {
                xs: 1.5,
                md: 2.5,
              },
            }}
          >
            {statCards.map(
              (card) => (
                <Card
                  key={
                    card.label
                  }
                  sx={{
                    minWidth: 0,

                    overflow:
                      "hidden",

                    borderRadius:
                      {
                        xs: 3,
                        md: 4,
                      },

                    border:
                      `1px solid ${alpha(
                        card.color,
                        0.15
                      )}`,

                    background:
                      `linear-gradient(
                        145deg,
                        #fff,
                        ${alpha(
                          card.color,
                          0.07
                        )}
                      )`,

                    boxShadow:
                      "0 14px 36px rgba(15,23,42,.07)",

                    animation:
                      `${fadeInUp} .35s ease both`,
                  }}
                >
                  <CardContent
                    sx={{
                      p: {
                        xs: 1.25,
                        sm: 1.7,
                        md: 2,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          width: {
                            xs: 39,
                            md: 46,
                          },

                          height: {
                            xs: 39,
                            md: 46,
                          },

                          bgcolor:
                            alpha(
                              card.color,
                              0.12
                            ),

                          color:
                            card.color,

                          flexShrink:
                            0,
                        }}
                      >
                        {card.icon}
                      </Avatar>

                      <Box
                        sx={{
                          minWidth:
                            0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display:
                              "block",

                            color:
                              "text.secondary",

                            fontWeight:
                              800,

                            overflow:
                              "hidden",

                            textOverflow:
                              "ellipsis",

                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {
                            card.label
                          }
                        </Typography>

                        <Typography
                          sx={{
                            color:
                              BRAND.navy,

                            fontWeight:
                              950,

                            fontSize:
                              {
                                xs: 18,
                                md: 22,
                              },

                            overflow:
                              "hidden",

                            textOverflow:
                              "ellipsis",

                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {
                            card.value
                          }
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )
            )}
          </Box>

          <PaymentRequestsPanel
            token={token}
            lang={lang}
            TT={TT}
          />

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 1.5,

                borderRadius: 3,
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={
                    handleRefresh
                  }
                >
                  {TT.retry}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Card
            sx={{
              mb: {
                xs: 1.5,
                md: 2.5,
              },

              borderRadius: {
                xs: 3.5,
                md: 4.5,
              },

              border:
                "1px solid rgba(226,232,240,.9)",

              boxShadow:
                "0 14px 36px rgba(15,23,42,.07)",

              background:
                "rgba(255,255,255,.94)",
            }}
          >
            <CardContent
              sx={{
                p: {
                  xs: 1.3,
                  sm: 1.8,
                  md: 2.2,
                },
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  lg: "row",
                }}
                spacing={1.3}
                alignItems="stretch"
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={
                    TT.search
                  }
                  value={
                    searchTerm
                  }
                  onChange={(
                    event
                  ) =>
                    setSearchTerm(
                      event.target
                        .value
                    )
                  }
                  InputProps={{
                    startAdornment:
                      (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),

                    endAdornment:
                      searchTerm ? (
                        <InputAdornment position="end">
                          <Tooltip title="Temizle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setSearchTerm(
                                  ""
                                )
                              }
                            >
                              <ClearIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                  }}
                  sx={{
                    flex: 1,

                    minWidth: 0,

                    "& .MuiOutlinedInput-root":
                      {
                        minHeight:
                          48,

                        borderRadius:
                          3,

                        bgcolor:
                          "#fff",

                        fontWeight:
                          750,
                      },
                  }}
                />

                <Box
                  sx={{
                    overflowX:
                      "auto",

                    pb: 0.2,
                  }}
                >
                  <ToggleButtonGroup
                    exclusive
                    value={
                      filterStatus
                    }
                    onChange={(
                      _,
                      value
                    ) =>
                      value &&
                      setFilterStatus(
                        value
                      )
                    }
                    sx={{
                      minWidth:
                        "max-content",

                      "& .MuiToggleButton-root":
                        {
                          minHeight:
                            46,

                          px: {
                            xs: 1.4,
                            md: 2,
                          },

                          borderRadius:
                            "13px !important",

                          mx: 0.35,

                          textTransform:
                            "none",

                          fontWeight:
                            950,
                        },
                    }}
                  >
                    <ToggleButton value="all">
                      {TT.all}
                    </ToggleButton>

                    <ToggleButton value="yeni">
                      {TT.new}
                    </ToggleButton>

                    <ToggleButton value="hazirlaniyor">
                      {TT.preparing}
                    </ToggleButton>

                    <ToggleButton value="tamamlandi">
                      {TT.completed}
                    </ToggleButton>

                    <ToggleButton value="iptal">
                      {TT.cancelled}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <ReportsPanel
            statsPeriod={
              statsPeriod
            }
            setStatsPeriod={
              setStatsPeriod
            }
            statsData={
              statsData
            }
            productSales={
              productSales
            }
            loading={
              statsLoading
            }
            TT={TT}
            lang={lang}
          />

          <StockPanel
            stocks={stocks}
            setStocks={
              setStocks
            }
            loading={
              stockLoading
            }
            onSave={
              saveStock
            }
            onDelete={
              deleteStock
            }
            TT={TT}
          />

          <Card
            sx={{
              mb: 1.2,

              borderRadius: {
                xs: 3.5,
                md: 4.5,
              },

              border:
                "1px solid rgba(226,232,240,.9)",

              background:
                "linear-gradient(145deg,#fff,#f8fafc)",

              boxShadow:
                "0 14px 36px rgba(15,23,42,.06)",
            }}
          >
            <CardContent
              sx={{
                p: {
                  xs: 1.3,
                  sm: 1.8,
                  md: 2.2,
                },
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      color:
                        BRAND.navy,

                      fontWeight:
                        950,

                      fontSize: {
                        xs: 18,
                        md: 22,
                      },
                    }}
                  >
                    🧾{" "}
                    {TT.orderList}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        "text.secondary",

                      fontWeight:
                        700,
                    }}
                  >
                    {
                      TT.orderListSub
                    }
                  </Typography>
                </Box>

                <Chip
                  label={`${filteredOrders.length} ${TT.orderCount.toLowerCase()}`}
                  color="primary"
                  sx={{
                    borderRadius:
                      999,

                    fontWeight:
                      950,
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          {paginatedOrders.length ===
          0 ? (
            <Card
              sx={{
                py: {
                  xs: 4,
                  md: 6,
                },

                px: 2,

                textAlign:
                  "center",

                borderRadius: {
                  xs: 3.5,
                  md: 4.5,
                },

                border:
                  "1px solid rgba(226,232,240,.9)",

                boxShadow:
                  "0 14px 36px rgba(15,23,42,.06)",
              }}
            >
              <Avatar
                sx={{
                  width: 68,
                  height: 68,

                  mx: "auto",
                  mb: 1.4,

                  bgcolor: alpha(
                    theme.palette
                      .primary.main,
                    0.1
                  ),

                  color:
                    "primary.main",
                }}
              >
                <ReceiptLongIcon
                  sx={{
                    fontSize: 34,
                  }}
                />
              </Avatar>

              <Typography
                sx={{
                  color:
                    BRAND.navy,

                  fontWeight: 950,
                }}
              >
                {searchTerm ||
                filterStatus !==
                  "all"
                  ? TT.noFiltered
                  : TT.noOrders}
              </Typography>
            </Card>
          ) : (
            <Stack
              spacing={{
                xs: 1.2,
                md: 1.6,
              }}
              sx={{
                minWidth: 0,
              }}
            >
              {paginatedOrders.map(
                (order) => (
                  <OrderAccordion
                    key={
                      order.id
                    }
                    order={order}
                    TT={TT}
                    lang={lang}
                    onStatusChange={
                      handleStatusUpdate
                    }
                    onKitchenPrint={
                      setKitchenPrintOrder
                    }
                    onCancel={(
                      id
                    ) => {
                      setSelectedOrderId(
                        id
                      );

                      setCancelDialogOpen(
                        true
                      );
                    }}
                    onDelete={
                      deleteOrderPermanently
                    }
                  />
                )
              )}
            </Stack>
          )}

          {pageCount > 1 && (
            <Box
              sx={{
                mt: 2.5,

                display: "flex",

                justifyContent:
                  "center",

                overflowX:
                  "auto",

                pb: 1,
              }}
            >
              <Pagination
                count={
                  pageCount
                }
                page={page}
                onChange={(
                  _,
                  value
                ) =>
                  setPage(value)
                }
                color="primary"
                size="large"
                sx={{
                  "& .MuiPagination-ul":
                    {
                      flexWrap:
                        "nowrap",
                    },

                  "& .MuiPaginationItem-root":
                    {
                      borderRadius:
                        2,

                      fontWeight:
                        850,
                    },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <WaiterCallsDialog
        open={
          showWaiterDialog
        }
        onClose={() =>
          setShowWaiterDialog(
            false
          )
        }
        calls={waiterCalls}
        unreadCount={
          unreadWaiterCount
        }
        onMarkRead={
          markWaiterCallAsRead
        }
        TT={TT}
        lang={lang}
      />

      <KitchenPrintDialog
        open={Boolean(kitchenPrintOrder)}
        order={kitchenPrintOrder}
        TT={TT}
        lang={lang}
        paperSize={kitchenPaperSize}
        onPaperSizeChange={setKitchenPaperSize}
        includePrices={kitchenIncludePrices}
        onIncludePricesChange={setKitchenIncludePrices}
        onClose={() => setKitchenPrintOrder(null)}
        onPrintResult={(opened) => {
          setSnackbar({
            open: true,
            message: opened
              ? TT.printOpened
              : TT.popupBlocked,
            severity: opened ? "success" : "warning",
          });
        }}
      />

      <Dialog
        open={
          cancelDialogOpen
        }
        onClose={() =>
          setCancelDialogOpen(
            false
          )
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 950,
          }}
        >
          {TT.cancelTitle}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {TT.cancelText}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
          }}
        >
          <Button
            onClick={() =>
              setCancelDialogOpen(
                false
              )
            }
            sx={{
              textTransform:
                "none",

              fontWeight: 850,
            }}
          >
            {TT.giveUp}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (
                !selectedOrderId
              ) {
                return;
              }

              await handleStatusUpdate(
                selectedOrderId,
                "iptal"
              );

              setCancelDialogOpen(
                false
              );

              setSelectedOrderId(
                null
              );
            }}
            sx={{
              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 950,
            }}
          >
            {TT.cancel}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={
          4200
        }
        onClose={() =>
          setSnackbar(
            (current) => ({
              ...current,

              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "bottom",

          horizontal:
            "center",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,

                open: false,
              })
            )
          }
          sx={{
            borderRadius: 3,

            fontWeight: 850,

            boxShadow: 8,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
